/*
@license
  Concept by RoarTheme (https://roartheme.co)
  Access unminified JS in assets/theme.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://roartheme.co/blogs/concept/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/

window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 767px)',
  motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isTouch: ('ontouchstart' in window) || (navigator.MaxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0),
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false
};

theme.supportsPassive = false;

try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function() {
      theme.supportsPassive = true;
    }
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) {}

document.documentElement.classList.add(theme.config.isTouch ? 'touch' : 'no-touch');
console.log(theme.settings.themeName + ' theme (' + theme.settings.themeVersion + ') by RoarTheme | Learn more at https://roartheme.co');

(function () {
  /*============================================================================
    Things that require DOM to be ready
  ==============================================================================*/
  theme.DOMready = function (callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  }

  theme.headerGroup = {
    rounded: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group .section--rounded'));
    },
    sections: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
    },
    init: () => {
      let previousSection = null;
      const rounded = theme.headerGroup.rounded();
      const sections = theme.headerGroup.sections();

      sections.forEach((shopifySection, index) => {
        const section = shopifySection.querySelector('.section');
        if (section) {
          section.classList.remove('section--next-rounded');
          section.classList.remove('section--first-rounded');
          section.classList.remove('section--last-rounded');

          if (section.classList.contains('section--rounded')) {
            if (index === 0) {
              section.classList.add('section--first-rounded');
            }
  
            if (index === rounded.length - 1) {
              section.classList.add('section--last-rounded');
            }

            if (previousSection && previousSection.classList.contains('section--rounded')) {
              previousSection.classList.add('section--next-rounded');
            }
          }

          if (index === sections.length - 1) {
            const nextSection = document.querySelector('.main-content .shopify-section:first-child .section');
            if (nextSection && nextSection.classList.contains('section--rounded')) {
              section.classList.add('section--next-rounded');
            }
          }

          previousSection = section;
        }
      });
    }
  };

  theme.a11y = {
    trapFocusHandlers: {},

    getFocusableElements: (container) => {
      return Array.from(
        container.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
        )
      );
    },

    trapFocus: (container, elementToFocus = container) => {
      const elements = theme.a11y.getFocusableElements(container);
      const first = elements[0];
      const last = elements[elements.length - 1];

      theme.a11y.removeTrapFocus();

      theme.a11y.trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;

        document.addEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.addEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);

      elementToFocus.focus();

      if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    },
    removeTrapFocus: (elementToFocus = null) => {
      document.removeEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);
      document.removeEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);

      if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus();
    }
  };

  theme.utils = {
    throttle: (callback) => {
      let requestId = null, lastArgs;
      const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
      };
      const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
          requestId = requestAnimationFrame(later(this));
        }
      };
      throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
      };
      return throttled;
    },

    debounce: (fn, wait) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        const done = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, done);
            resolve(event);
          }
        };
        element.addEventListener(eventName, done);
      });
    },

    fetchConfig: (type = 'json') => {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
      };
    },

    postLink: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};
    
      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);
    
      for (const key in params) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", key);
        hiddenField.setAttribute("value", params[key]);
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    sectionId: (element) => {
      if (element.hasAttribute('data-section-id')) return element.dataset.sectionId;

      element = element.classList.contains('shopify-section') ? element : element.closest('.shopify-section');
      return element.id.replace('shopify-section-', '');
    },

    imageLoaded: (imageOrArray) => {
      if (!imageOrArray) {
        return Promise.resolve();
      }
      imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
      return Promise.all(imageOrArray.map((image) => {
        return new Promise((resolve) => {
          if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
            resolve();
          } else {
            image.onload = () => resolve();
          }
        });
      }));
    },

    visibleMedia: (media) => {
      return Array.from(media).find((item) => {
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
      });
    },

    setScrollbarWidth: () => {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      if (scrollbarWidth > 0) {
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      }
    }
  };

  theme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: 'cartUpdate',
      quantityUpdate: 'quantityUpdate',
      variantChange: 'variantChange',
      cartError: 'cartError',
      facetUpdate: 'facetUpdate',
    },

    subscribers: {},

    subscribe: (eventName, callback) => {
      if (theme.pubsub.subscribers[eventName] === undefined) {
        theme.pubsub.subscribers[eventName] = [];
      }

      theme.pubsub.subscribers[eventName] = [...theme.pubsub.subscribers[eventName], callback];

      return function unsubscribe() {
        theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[eventName].filter((cb) => {
          return cb !== callback
        });
      }
    },

    publish: (eventName, data) => {
      if (theme.pubsub.subscribers[eventName]) {
        theme.pubsub.subscribers[eventName].forEach((callback) => {
          callback(data);
        })
      }
    }
  };

  theme.scrollShadow = {
    Updater: (function () {
      class Updater {
        constructor(targetElement) {
          this.cb = () => this.update(targetElement);
          this.rO = new ResizeObserver(this.cb);
          this.mO = new MutationObserver(() => this.on(this.el));
        }
        on(element) {
          if (this.el) {
            this.el.removeEventListener("scroll", this.cb);
            this.rO.disconnect();
            this.mO.disconnect();
          }
          if (!element)
            return;
          if (element.nodeName === "TABLE" && !/scroll|auto/.test(getComputedStyle(element).getPropertyValue("overflow"))) {
            this.rO.observe(element);
            element = element.tBodies[0];
          }
          element.addEventListener("scroll", this.cb);
          [element, ...element.children].forEach((el) => this.rO.observe(el));
          this.mO.observe(element, { childList: true });
          this.el = element;
        }
        update(targetElement) {
          const scrollTop = this.el.scrollTop;
          const scrollBottom = this.el.scrollHeight - this.el.offsetHeight - this.el.scrollTop;
          const scrollLeft = this.el.scrollLeft;
          const scrollRight = this.el.scrollWidth - this.el.offsetWidth - this.el.scrollLeft * (theme.config.rtl ? -1 : 1);
          let cssText = `--t: ${scrollTop > 0 ? 1 : 0}; --b: ${scrollBottom > 0 ? 1 : 0}; --l: ${scrollLeft > 0 ? 1 : 0}; --r: ${scrollRight > 0 ? 1 : 0};`;
          if (this.el.nodeName === "TBODY") {
            const clientRect = this.el.getBoundingClientRect();
            const rootClientRect = this.el.parentElement.getBoundingClientRect();
            cssText += `top: ${clientRect.top - rootClientRect.top}px; bottom: ${rootClientRect.bottom - clientRect.bottom}px; left: ${clientRect.left - rootClientRect.left}px; right: ${rootClientRect.right - clientRect.right}px;`;
          }
          requestAnimationFrame(() => {
            targetElement.style.cssText = cssText;
          });
        }
      }

      return Updater;
    })()
  };

  theme.cookiesEnabled = function () {
    let cookieEnabled = navigator.cookieEnabled;

    if (!cookieEnabled) {
      document.cookie = 'testcookie';
      cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
    }
    return cookieEnabled;
  };

  theme.isStorageSupported = function (type) {
    // Return false if we are in an iframe without access to sessionStorage
    if (window.self !== window.top) {
      return false;
    }

    const testKey = 'test';
    let storage;
    if (type === 'session') {
      storage = window.sessionStorage;
    }
    if (type === 'local') {
      storage = window.localStorage;
    }

    try {
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return true;
    }
    catch (error) {
      // Do nothing, this may happen in Safari in incognito mode
      return false;
    }
  };

  theme.getElementWidth = function (element, value) {
    const style = window.getComputedStyle(element);

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.getBoundingClientRect().width;
    text.remove();

    return width;
  };

  theme.HoverButton = (function () {
    class HoverButton {
      constructor(container) {
        this.container = container;
      }

      load() {
        if (theme.config.isTouch || document.body.getAttribute('data-button_hover') === 'none') return;

        this.container.addEventListener('mouseenter', this.onEnterHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onEnterHandler(event) {
        const hoverButton = event.currentTarget;
        const btnFill = hoverButton.querySelector('[data-fill]');

        if (btnFill) {
          Motion.animate(btnFill, { y: ['76%', '0%'] }, { duration: 0.6 });
        }
      }

      onLeaveHandler(event) {
        const hoverButton = event.currentTarget;
        const btnFill = hoverButton.querySelector('[data-fill]');

        if (btnFill) {
          Motion.animate(btnFill, { y: '-76%' }, { duration: 0.6 });
        }
      }

      unload() {
        // todo
      }
    }

    return HoverButton;
  })();

  theme.MagnetButton = (function () {
    const config = {
      magnet: 10
    };

    class MagnetButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.dataset.magnet ? parseInt(container.dataset.magnet) : config.magnet;
      }

      load() {
        if (theme.config.isTouch || document.body.getAttribute('data-button_hover') === 'none') return;

        this.container.addEventListener('mousemove', this.onMoveHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onMoveHandler(event) {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        const target = event.currentTarget;
        const btnText = target.querySelector('[data-text]');

        const bounding = target.getBoundingClientRect();

        if (btnText) {
          Motion.animate(btnText, {
            x: ((event.clientX - bounding.left) / target.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - bounding.top) / target.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(target, {
            x: ((event.clientX - bounding.left) / target.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - bounding.top) / target.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      onLeaveHandler(event) {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        const target = event.currentTarget;
        const btnText = target.querySelector('[data-text]');

        if (btnText) {
          Motion.animate(btnText, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(target, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      unload() {
        // todo
      }
    }

    return MagnetButton;
  })();

  theme.RevealButton = (function () {
    const config = {
      magnet: 25
    };

    class RevealButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.dataset.magnet ? parseInt(container.dataset.magnet) : config.magnet;
      }

      load() {
        if (theme.config.isTouch) return;

        this.container.addEventListener('mousemove', this.onMoveHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onMoveHandler(event) {
        const target = event.currentTarget;
        const bounding = target.getBoundingClientRect();
        const btnReveal = target.querySelector('[data-reveal]');

        if (btnReveal) {
          Motion.animate(btnReveal, {
            opacity: 1,
            x: ((event.clientX - bounding.left) / target.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - bounding.top) / target.offsetHeight - 0.5) * this.magnet,
          }, { duration: 0.2, easing: 'steps(2, start)' });
        }
      }

      onLeaveHandler(event) {
        const target = event.currentTarget;
        const btnReveal = target.querySelector('[data-reveal]');

        if (btnReveal) {
          Motion.animate(btnReveal, { x: 0, y: 0, opacity: 0 }, { duration: 0.2, easing: [0.61, 1, 0.88, 1] });
        }
      }

      unload() {
        // todo
      }
    }

    return RevealButton;
  })();

  theme.Carousel = (function () {
    class Carousel {
      constructor(container, options, navigation) {
        this.container = container;
        this.options = options;
        this.prevButton = navigation.previous;
        this.nextButton = navigation.next;
      }

      load() {
        this.slider = new Flickity(this.container, this.options);

        this.prevButton.addEventListener('click', this.onPrevButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onNextButtonClick.bind(this));

        this.slider.on('dragStart', this.onDragStartHandler.bind(this));
        this.slider.on('select', this.onSelectHandler.bind(this));
      }

      onDragStartHandler() {
        this.prevRemoveTransform();
        this.nextRemoveTransform();
      }

      onSelectHandler() {
        if (!this.slider.slides[this.slider.selectedIndex - 1]) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = false;
        }
        else if (!this.slider.slides[this.slider.selectedIndex + 1]) {
          this.prevButton.disabled = false;
          this.nextButton.disabled = true;
        }
        else {
          this.prevButton.disabled = false;
          this.nextButton.disabled = false;
        }
      }

      onPrevButtonClick(event) {
        event.preventDefault();

        this.slider.previous();
        this.nextRemoveTransform();
      }

      onNextButtonClick(event) {
        event.preventDefault();

        this.slider.next();
        this.prevRemoveTransform();
      }

      prevRemoveTransform() {
        this.prevButton.style.transform = null;
        this.prevButton.querySelector('[data-fill]').style.transform = null;
      }

      nextRemoveTransform() {
        this.nextButton.style.transform = null;
        this.nextButton.querySelector('[data-fill]').style.transform = null;
      }

      unload() {
        // todo
      }
    }

    return Carousel;
  })();

  // Delay JavaScript until user interaction
  theme.initWhenVisible = (function() {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.loadScriptTimer = setTimeout(callback, delay);
        this.userInteractionEvents = ["mouseover", "mousemove", "keydown", "touchstart", "touchend", "touchmove", "wheel"];

        this.onScriptLoader = this.triggerScriptLoader.bind(this, callback);
        this.userInteractionEvents.forEach((event) => {
          window.addEventListener(event, this.onScriptLoader, {
            passive: !0
          });
        });
      }

      triggerScriptLoader(callback) {
        callback();
        clearTimeout(this.loadScriptTimer);
        this.userInteractionEvents.forEach((event) => {
          window.removeEventListener(event, this.onScriptLoader, {
            passive: !0
          });
        });
      }
    }

    return ScriptLoader;
  })();

  // Improve initial load time by skipping the rendering of offscreen content
  new theme.initWhenVisible(() => {
    document.body.removeAttribute('data-page-rendering');
  });

  theme.DOMready(theme.headerGroup.init);
  theme.DOMready(theme.utils.setScrollbarWidth);
  window.addEventListener('resize', theme.utils.throttle(theme.utils.setScrollbarWidth));

  /*============================================================================
    Things that don't require DOM to be ready
  ==============================================================================*/

  theme.config.hasSessionStorage = theme.isStorageSupported('session');
  theme.config.hasLocalStorage = theme.isStorageSupported('local');

  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (mql) => {
    if (mql.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    }
    else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  }
})();

// Prevent vertical scroll while using flickity sliders
new theme.initWhenVisible(() => {
  var e = !1;
  var t;

  document.body.addEventListener('touchstart', function (i) {
    if (!i.target.closest('.flickity-slider')) {
      return e = !1;
      void 0;
    }
    e = !0;
    t = {
      x: i.touches[0].pageX,
      y: i.touches[0].pageY
    }
  }, theme.supportsPassive ? { passive: true } : false);

  document.body.addEventListener('touchmove', function (i) {
    if (e && i.cancelable) {
      var n = {
        x: i.touches[0].pageX - t.x,
        y: i.touches[0].pageY - t.y
      };
      Math.abs(n.x) > Flickity.defaults.dragThreshold && i.preventDefault()
    }
  }, theme.supportsPassive ? { passive: false } : false);
});

class LoadingBar extends HTMLElement {
  constructor() {
    super();

    window.addEventListener('beforeunload', () => {
      document.body.classList.add('unloading');
    });

    window.addEventListener('DOMContentLoaded', () => {
      Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: 1 });

      document.body.classList.add('loaded');
      document.dispatchEvent(new CustomEvent('page:loaded'));
    });

    window.addEventListener('pageshow', (event) => {
      // Removes unload class when returning to page via history
      if (event.persisted) {
        document.body.classList.remove('unloading');
      }
    });
  }
}
customElements.define('loading-bar', LoadingBar);

/* class MouseCursor extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) return;

    this.config = {
      posX: 0,
      posY: 0,
    };

    document.addEventListener('mousemove', (event) => {
      this.config.posX += (event.clientX - this.config.posX) / 4;
      this.config.posY += (event.clientY - this.config.posY) / 4;

      this.style.setProperty('--x', `${this.config.posX}px`);
      this.style.setProperty('--y', `${this.config.posY}px`);
    });
  }
}
customElements.define('mouse-cursor', MouseCursor);*/

class CustomHeader extends HTMLElement {
  constructor() {
    super();
  }

  get allowTransparent() {
    if (document.querySelector('.shopify-section:first-child [allow-transparent-header]') && !document.querySelector('.header-promo-bar')) {
      return true;
    }

    return false;
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    new theme.initWhenVisible(this.setHeight.bind(this));

    if (this.allowTransparent) {
      this.headerSection.classList.add('header-transparent');
      this.headerSection.classList.add('no-animate');

      setTimeout(() => {
        this.headerSection.classList.remove('no-animate');
      }, 500);
    }
    else {
      this.headerSection.classList.remove('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--header-height', Math.round(this.clientHeight) + 'px');

      if (this.classList.contains('header--center')) {
        document.documentElement.style.setProperty('--header-nav-height', Math.round(document.getElementById('MenuToggle')?.clientHeight) + 'px');
      }
    });
  }
}
customElements.define('custom-header', CustomHeader, { extends: 'header' });

/* Sticky mobile description style change */
const stickyButton = document.querySelector('.description-container');
let isStuck = false;
const checkSticky = () => {
  if (stickyButton != null) {
    const stickyBottom = stickyButton.getBoundingClientRect().bottom;
    const viewportHeight = window.innerHeight;
    if (stickyBottom >= viewportHeight && !isStuck) {
      stickyButton.classList.add('stuck');
      isStuck = true;
    } else if (stickyBottom < viewportHeight && isStuck) {
      stickyButton.classList.remove('stuck');
      isStuck = false;
    }
  }
};
document.addEventListener('DOMContentLoaded', checkSticky);

class StickyHeader extends CustomHeader {
  constructor() {
    super();
  }

  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.dataset.stickyType = this.dataset.stickyType;

    if (this.isAlwaysSticky) {
      this.headerSection.classList.add('header-sticky');
    }
    let $this = this;
    if(document.querySelector('.collection.with-drawer .collection__topbar') != null) {
      setTimeout(function() {
        $this.headerSection.dataset.scrollHeight = document.querySelector('.collection.with-drawer .collection__topbar').getBoundingClientRect().top + window.scrollY;
      }, 500);
    } else {
      this.headerSection.dataset.scrollHeight = 'unset';
    }

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    if (scrollTop > (this.headerBounds.top + this.firstScrollTop + this.headerBounds.height)) {
      this.headerSection.classList.add('header-scrolled');
      document.documentElement.style.setProperty('--sticky-header-height', Math.round(this.clientHeight) + 'px');
    }
    else {
      this.headerSection.classList.remove('header-scrolled');
    }

    if (this.headerSection.dataset.scrollHeight == 'unset') {
      var scrollBottomDistance = this.headerBounds.bottom + this.firstScrollTop + 100;
    } else {
      var scrollBottomDistance = this.headerSection.dataset.scrollHeight;
    }

    let stickyCollectionBar = document.querySelector('.sticky-collection-bar');
    
    if (scrollTop > scrollBottomDistance) {
      if(stickyCollectionBar) {
        stickyCollectionBar.classList.add('visible');
      }
      if (scrollTop > this.currentScrollTop) {
     //   this.headerSection.classList.add('header-hidden');
      } else {
        this.headerSection.classList.remove('header-hidden');
      }
    } else {
      if(stickyCollectionBar) {
        stickyCollectionBar.classList.remove('visible');
      }
      this.headerSection.classList.remove('header-hidden');
    }

    this.currentScrollTop = scrollTop;

    /* Check sticky mobile description trigger */
    checkSticky();
    
  }
}
customElements.define('sticky-header', StickyHeader, { extends: 'header' });

class RevealLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.revealButton = new theme.RevealButton(this);
    this.revealButton.load();
  }
}
customElements.define('reveal-link', RevealLink, { extends: 'a' });

class HoverLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-link', HoverLink, { extends: 'a' });

class MagnetLink extends HoverLink {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-link', MagnetLink, { extends: 'a' });

class HoverButton extends HTMLButtonElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();

    if (this.type === 'submit' && this.form) {
      this.form.addEventListener('submit', () => this.setAttribute('aria-busy', 'true'));
    }

    window.addEventListener('pageshow', () => this.removeAttribute('aria-busy'));
    this.append(this.animationElement);
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement = this._contentElement || this.querySelector('.btn-text');
  }

  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild;
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (theme.config.isTouch || document.body.getAttribute('data-button_hover') === 'none') return;
    
    if (newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
      Motion.animate(this.animationElement.children, { transform: ['scale(1.6)', 'scale(0.6)'] }, { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity });
    }
    else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
    }
  }
}
customElements.define('hover-button', HoverButton, { extends: 'button' });

class MagnetButton extends HoverButton {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-button', MagnetButton, { extends: 'button' });

class HoverElement extends HTMLElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-element', HoverElement);

class MagnetElement extends HoverElement {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-element', MagnetElement);

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    }
    else {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  init() {
    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        on: {
          ready: function() {
            setTimeout(() => this.element.setAttribute('loaded', ''));
          }
        }
      });
  
      this.slider.on('change', this.onChange.bind(this));
      this.addEventListener('slider:previous', () => this.slider.previous());
      this.addEventListener('slider:next', () => this.slider.next());
      this.addEventListener('slider:play', () => this.slider.playPlayer());
      this.addEventListener('slider:pause', () => this.slider.pausePlayer());
      
      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  onChange() {
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }));
  }
}
customElements.define('announcement-bar', AnnouncementBar);

class AccordionsDetails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('toggle', this.onToggle);
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('details[is="accordion-details"]'));
  }

  onToggle(event) {
    this.items.forEach((item) => {
      if (item !== event.detail.current) {
        item.close();
      }
    });
  }
}
customElements.define('accordions-details', AccordionsDetails);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false;
      });
    }
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest('accordions-details');
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEventHandler();
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  close() {
    this._open = false;
    this.transition(false);
  }

  async transition(value) {
    this.style.overflow = 'hidden';

    if (value) {
      this.setAttribute('open', '');

      await Motion.timeline([
        [this, { height: [`${this.summaryElement.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.25, easing: 'ease' }],
        [this.contentElement, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0.15, at: '-0.1' }]
      ]).finished;
    }
    else {
      await Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this, { height: [`${this.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: '<', easing: 'ease' }]
      ]).finished;

      this.removeAttribute('open');
    }

    this.style.height = 'auto';
    this.style.overflow = 'visible';
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this } }));
  }
}
customElements.define('accordion-details', AccordionDetails, { extends: 'details' });

class FooterDetails extends AccordionDetails {
  constructor() {
    super();

    this.load();
    window.addEventListener('resize', this.load.bind(this));
    document.addEventListener('unmatchSmall', this.load.bind(this));
  }

  get designModeActive() {
    return theme.config.mqlSmall;
  }

  load() {
    if (theme.config.mqlSmall) {
      if (this.open) {
        this._open = false;
        this.removeAttribute('open');
        this.setAttribute('aria-expanded', 'false');
        this.classList.remove('active');
      }
    }
    else {
      this._open = true;
      this.setAttribute('open', '');
      this.setAttribute('aria-expanded', 'true');
      this.classList.add('active');
    }
  }
}
customElements.define('footer-details', FooterDetails, { extends: 'details' });

class GestureElement extends HTMLElement {
  constructor() {
    super();

    this.config = {
      thresholdY: Math.max(25, Math.floor(0.15 * window.innerHeight)),
      velocityThreshold: 10,
      disregardVelocityThresholdY: Math.floor(0.5 * this.clientHeight),
      pressThreshold: 8,
      diagonalSwipes: false,
      diagonalLimit: Math.tan(((45 * 1.5) / 180) * Math.PI),
      longPressTime: 500,
    };

    this.handlers = {
      panstart: [],
      panmove: [],
      panend: [],
      swipeup: [],
      swipedown: [],
      longpress: [],
    };

    this.addEventListener('touchstart', this.onTouchStart.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchmove', this.onTouchMove.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchend', this.onTouchEnd.bind(this), theme.supportsPassive ? { passive: true } : false);
  }

  on(type, fn) {
    if (this.handlers[type]) {
      this.handlers[type].push(fn);
      return {
        type,
        fn,
        cancel: () => this.off(type, fn),
      };
    }
  }

  off(type, fn) {
    if (this.handlers[type]) {
      const idx = this.handlers[type].indexOf(fn);
      if (idx !== -1) {
        this.handlers[type].splice(idx, 1);
      }
    }
  }

  fire(type, event) {
    for (let i = 0; i < this.handlers[type].length; i++) {
      this.handlers[type][i](event);
    }
  }

  onTouchStart(event) {
    this.thresholdY = this.config.thresholdY;
    this.disregardVelocityThresholdY = this.config.disregardVelocityThresholdY;
    this.touchStartY = event.changedTouches[0].screenY;
    this.touchMoveY = null;
    this.touchEndY = null;
    this.swipingDirection = null;

    // Long press.
    this.longPressTimer = setTimeout(() => this.fire('longpress', event), this.config.longPressTime);
    this.fire('panstart', event);
  }

  onTouchMove(event) {
    const touchMoveY = event.changedTouches[0].screenY - (this.touchStartY ?? 0);
    this.velocityY = touchMoveY - (this.touchMoveY ?? 0);
    this.touchMoveY = touchMoveY;
    const absTouchMoveY = Math.abs(this.touchMoveY);
    this.swipingVertical = absTouchMoveY > this.thresholdY;
    this.swipingDirection = this.swipingVertical ? 'vertical' : 'pre-vertical';

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer ?? undefined);
    }
    this.fire('panmove', event);
  }

  onTouchEnd(event) {
    this.touchEndY = event.changedTouches[0].screenY;
    this.fire('panend', event);
    clearTimeout(this.longPressTimer ?? undefined);

    const y = this.touchEndY - (this.touchStartY ?? 0);
    const absY = Math.abs(y);

    if (absY > this.thresholdY) {
      this.swipedVertical = this.config.diagonalSwipes ? y <= this.config.diagonalLimit : absY > this.thresholdY;
      if (this.swipedVertical) {
        if (y < 0) {
          // Upward swipe.
          if ((this.velocityY ?? 0) < -this.config.velocityThreshold || y < -this.disregardVelocityThresholdY) {
            this.fire('swipeup', event);
          }
        } else {
          // Downward swipe.
          if ((this.velocityY ?? 0) > this.config.velocityThreshold || y > this.disregardVelocityThresholdY) {
            this.fire('swipedown', event);
          }
        }
      }
    }
  }
}
customElements.define('gesture-element', GestureElement);

class OverlayElement extends HTMLElement {
  constructor() {
    super();

 //   this.addEventListener('mousemove', this.onMouseMove);
 //   this.addEventListener('mouseleave', this.onMouseLeave);
 //   this.addEventListener('mousedown', this.onMouseDown);
 //   this.addEventListener('mouseup', this.onMouseUp);
  }

/*  get cursor() {
    return document.querySelector('mouse-cursor');
  }

  onMouseMove() {
    this.cursor.classList.add('active');
  }
  onMouseLeave() {
    this.cursor.classList.remove('active');
  }
  onMouseDown() {
    this.cursor.classList.add('pressed');
  }
  onMouseUp() {
    this.cursor.classList.remove('pressed');
  }*/
}
customElements.define('overlay-element', OverlayElement);

const lockLayerCount = new WeakMap();
class ModalElement extends HTMLElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'modal:afterHide',
      afterShow: 'modal:afterShow'
    };

    this.classes = {
      open: 'has-modal-open',
      opening: 'has-modal-opening'
    };
  }

  static get observedAttributes() {
    return ['id', 'open'];
  }

  get shouldLock() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get overlay() {
    return this._overlay = this._overlay || this.querySelector('.fixed-modal');
  }

  get gesture() {
    return this._gesture = this._gesture || this.querySelector('gesture-element');
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get focusElement() {
    return this.querySelector('button');
  }

  connectedCallback() {
    this.abortController = new AbortController();
    
    this.controls.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this), { signal: this.abortController.signal }));
    document.addEventListener('keyup', (event) => event.code === 'Escape' && this.hide(), { signal: this.abortController.signal });

    if (this.gesture) {
      this.gestureConfig = {
        animationFrame: null,
        moveY: 0,
        maxGestureDistance: 0,
        endPoint: 0,
        layerHeight: null
      };

      this.gestureWrap = this.gesture.parentElement;

      setTimeout(() => {
        this.gesture.on('panstart', this.onPanStart.bind(this));
        this.gesture.on('panmove', this.onPanMove.bind(this));
        this.gesture.on('panend', this.onPanEnd.bind(this));
      }, 75);
    }

  /*  if (Shopify.designMode && this.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', (event) => this.show(null, !event.detail.load), { signal: this.abortController.signal });
      section.addEventListener('shopify:section:deselect', this.hide.bind(this), { signal: this.abortController.signal });
    }*/
  }

  disconnectedCallback() {
    this.abortController?.abort();

    /*
    if (this.parentElement === document.body && this.originalParentBeforeAppend) {
      this.originalParentBeforeAppend.appendChild(this);
      this.originalParentBeforeAppend = null;
    }
    */
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));
        if (oldValue === null && (newValue === "" || newValue === 'immediate')) {
          this.hidden = false;
          this.removeAttribute('inert');

          this.originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this.originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this.showTransition(newValue !== 'immediate') || Promise.resolve();
          showTransitionPromise.then(() => {
            this.afterShow();
            this.dispatchEvent(new CustomEvent(this.events.afterShow, { bubbles: true }));
          });
        }
        else if (oldValue !== null && newValue === null) {
          this.setAttribute('inert', '');

          const hideTransitionPromise = this.hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            this.afterHide();
            
            if (!this.hasAttribute('inert')) return;

            if (this.parentElement === document.body && this.originalParentBeforeAppend) {
              this.originalParentBeforeAppend.appendChild(this);
              this.originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent(this.events.afterHide, { bubbles: true }));

            this.hidden = true;
          });
        }
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));

        break;
    }
  }

  onButtonClick(event) {
    event.preventDefault();

    this.open ? this.hide() : this.show(event.currentTarget);
  }

  hide() {
    if (!this.open) return;

    this.beforeHide();
    this.resetGesture();
    this.removeAttribute('open');

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }
  show(activeElement = null, animate = true) {
    if (this.open) return;

    this.beforeShow();
    this.activeElement = activeElement;
    this.setAttribute('open', animate ? '' : 'immediate');

    if (this.shouldLock) {
      document.body.classList.add(this.classes.opening);
    }

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  beforeHide() { }
  beforeShow() { }

  afterHide() {
    setTimeout(() => {
 //     theme.a11y.removeTrapFocus(this.activeElement);
      if (this.shouldLock) {
        lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) - 1);

        document.body.classList.toggle(this.classes.open, lockLayerCount.get(ModalElement) > 0);
      }
    });
  }
  afterShow() {
    theme.a11y.trapFocus(this, this.focusElement);
    if (this.shouldLock) {
      lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) + 1);

      document.body.classList.remove(this.classes.opening);
      document.body.classList.add(this.classes.open);
    }
  }

  showTransition() {
    setTimeout(() => {
      this.setAttribute('active', '');
    }, 75);
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }
  hideTransition() {
    this.removeAttribute('active');
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }

  resetGesture() {
    if (this.gesture) {
      this.gestureWrap.style.transform = '';
      this.gestureWrap.style.transition = '';
      this.overlay.style.opacity = '';
      this.overlay.style.transition = '';
    }
  }

  onPanStart() {
    this.removeTransition(this.gestureWrap, 'transform');

    if (this.hasAttribute('open')) {
      if (this.gestureConfig.layerHeight === null) {
        this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
      }
      this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;
      this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;
    }
  }

  onPanMove() {
    if (this.gestureConfig.animationFrame) return;

    this.gestureConfig.animationFrame = requestAnimationFrame(() => {
      const clamp = (a, min = 0, max = 1) => Math.min(max, Math.max(min, a));
      const invlerp = (x, y, a) => clamp((a - x) / (y - x));

      // Give an indication of whether we've passed the swiping threshold.
      this.gestureWrap.style.transition = 'transform .1s linear';
      this.overlay.style.transition = 'opacity .1s linear';

      if (this.hasAttribute('open')) {
        if (this.gestureConfig.layerHeight === null) {
          this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
        }

        this.gestureConfig.moveY = this.gesture.touchMoveY;
        this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;

        if (this.gestureConfig.moveY >= 0) {
          this.gestureWrap.style.transform = `translateY(${Math.min(
            this.gestureConfig.moveY,
            this.gestureConfig.maxGestureDistance
          )}px)`;

          this.overlay.style.opacity = invlerp(this.gestureConfig.layerHeight, 0, this.gestureConfig.moveY);
        }
        else {
          this.gestureWrap.style.transform = 'translateY(0)';
          this.gestureWrap.style.transition = '';
          this.overlay.style.opacity = '1';
        }
      }

      this.gestureConfig.animationFrame = null;
    });
  }

  onPanEnd() {
    this.gestureConfig.animationFrame === null || cancelAnimationFrame(this.gestureConfig.animationFrame);
    this.gestureConfig.animationFrame = null;
    this.gestureWrap.style.transition = 'transform .1s linear';

    if (this.gestureConfig.layerHeight === null) {
      this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
    }

    this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;

    if (this.hasAttribute('open')) {
      this.gestureConfig.moveY = this.gesture.touchMoveY;

      if (this.gestureConfig.moveY < this.gestureConfig.endPoint) {
        this.gestureWrap.style.transform = 'translateY(0)';
      }
      else {
        this.gestureWrap.style.transform = `translateY(${this.gestureConfig.layerHeight}px)`;

        this.gestureWrap.addEventListener(
          this.whichTransitionEvent(),
          () => {
            this.hide();
          },
          { once: true }
        );
      }
    }
  }

  removeTransition(node, transition) {
    const match = node.style.transition.match(new RegExp('(?:^|,)\\s*' + transition + '(?:$|\\s|,)[^,]*', 'i'));
    if (match) {
      const transitionArray = node.style.transition.split('');
      transitionArray.splice(match.index, match[0].length);
      node.style.transition = transitionArray.join('');
    }
  }

  whichTransitionEvent() {
    let t;
    const el = document.createElement('fakeelement');
    const transitions = {
      WebkitTransition: 'webkitTransitionEnd',
      MozTransition: 'transitionend',
      MSTransition: 'msTransitionEnd',
      OTransition: 'oTransitionEnd',
      transition: 'transitionEnd',
    };

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }
}
customElements.define('modal-element', ModalElement);
lockLayerCount.set(ModalElement, 0);

class DrawerElement extends ModalElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'drawer:afterHide',
      afterShow: 'drawer:afterShow'
    };
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('drawer-element', DrawerElement);

class MenuDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return this._menuItems = this._menuItems || this.querySelectorAll('.drawer__menu:not(.active)>li');
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      Motion.animate(this.menuItems, { transform: ['translateX(-20px)', 'translateX(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(0.1) }).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'));
      });
    }, 300);
  }

  afterHide() {
    super.afterHide();

    setTimeout(() => {
      this.querySelectorAll('details[is=menu-details]').forEach((subMenu) => {
        subMenu.onCloseButtonClick();
      });
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class ShareDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return this._menuItems = this._menuItems || this.querySelectorAll('.share-buttons>li');
  }

  get urlToShare() {
    const urlInput = this.querySelector('input[type=hidden]');
    return urlInput ? urlInput.value : document.location.href;
  }

  connectedCallback() {
    if (navigator.share) {
      this.controls.forEach((button) => button.addEventListener('click', this.shareTo.bind(this)));
    }
    else {
      super.connectedCallback();
    }
  }

  shareTo(event) {
    event.preventDefault();
    navigator.share({ url: this.urlToShare, title: document.title })
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      Motion.animate(this.menuItems, { transform: ['translateY(2.5rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(0.1) }).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'));
      });
    }, 300);
  }
}
customElements.define('share-drawer', ShareDrawer);

class MenuDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick() {
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation);
      }
      else {
        this.removeAttribute('open');
      }
    }

    requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-details', MenuDetails, { extends: 'details' });

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.input = this.querySelector('input');
    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });

    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)), { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      this.input.quantity = this.input.quantity + 1;
    }
    else {
      this.input.quantity = this.input.quantity - 1;
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('quantity-selector', QuantitySelector);

class QuantityInput extends HTMLInputElement {
  constructor() {
    super();

    this.addEventListener('input', this.onValueInput);
    this.addEventListener('change', this.onValueChanged);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('focus', this.select);
  }

  get quantity() {
    return parseInt(this.value);
  }

  set quantity(quantity) {
    const isNumeric = (typeof quantity === 'number' || typeof quantity === 'string' && quantity.trim() !== '') && !isNaN(quantity);
    if (quantity === '') return;

    if (!isNumeric || quantity < 0) {
      quantity = parseInt(quantity) || 1;
    }

    this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
    this.size = Math.max(this.value.length + 1, 2);
  }

  onValueInput() {
    this.quantity = this.value;
  }

  onValueChanged() {
    if (this.value === '') {
      this.quantity = 1;
    }
  }

  onKeyDown(event) {
    event.stopPropagation();

    const quantity = this.quantity;
    if (event.key === "ArrowUp") {
      this.quantity = this.quantity + 1;
    } else if (event.key === "ArrowDown") {
      this.quantity = this.quantity - 1;
    }
  }
}
customElements.define('quantity-input', QuantityInput, { extends: 'input' });

class CartCount extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText);
  }

  onCartUpdate(event) {
    if (event.cart.errors) return;

    this.innerText = event.cart.item_count;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('cart-count', CartCount);

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    if (theme.config.hasLocalStorage === false) {
      this.hidden = true;
      return;
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 1500 });
    }
    else {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
  }

  init() {
    fetch(this.dataset.url + this.getQueryString())
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        const recommendations = sectionInnerHTML.querySelector('recently-viewed');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(new CustomEvent('recentlyViewed:loaded'));
          document.dispatchEvent(new CustomEvent('recentlyViewed:loaded'));
        }
        else {
          this.closest('.recently-section')?.remove();
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  getQueryString() {
    let recentlyViewed = '[]';
    if (theme.config.hasLocalStorage) {
      recentlyViewed = window.localStorage.getItem(`${theme.settings.themeName}:recently-viewed`) || '[]';
    }

    const items = new Set(JSON.parse(recentlyViewed));
    if (this.dataset.productId) {
      items.delete(parseInt(this.dataset.productId));
    }
    return Array.from(items.values(), (item) => `id:${item}`).slice(0, parseInt(this.dataset.limit || 4)).join(' OR ');
  }
}
customElements.define('recently-viewed', RecentlyViewed);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 1500 });
    }
    else {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
  }

  init() {
    fetch(this.dataset.url)
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
          document.dispatchEvent(new CustomEvent('recommendations:loaded'));
        }
        else {
          this.closest('.shopify-section').remove();
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class ProductComplementary extends HTMLElement {
  constructor() {
    super();
  }

  get container() {
    return this.closest('product-recommendations');
  }

  get prevButton() {
    return this.container.querySelector('[data-prev-button]');
  }

  get nextButton() {
    return this.container.querySelector('[data-next-button]');
  }

  connectedCallback() {
    if (this.innerHTML.trim().length) {
      if (this.classList.contains('flickity')) {
        this.carousel = new theme.Carousel(this, {
          prevNextButtons: false,
          adaptiveHeight: true,
          pageDots: false,
          contain: true,
          cellAlign: 'center',
          rightToLeft: theme.config.rtl,
        }, {
          previous: this.prevButton,
          next: this.nextButton
        });

        this.carousel.load();
      }
    }
    else {
      this.container.hidden = true;
    }
  }

  disconnectedCallback() {
    if (this.carousel) {
      this.carousel.unload();
    }
  }
}
customElements.define('product-complementary', ProductComplementary);

class AnimateElement extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this.beforeInit();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get immediate() {
    return this.hasAttribute('data-immediate');
  }

  get media() {
    return Array.from(this.querySelectorAll('img, iframe, svg, g-map'));
  }

  get type() {
    return this.dataset.animate || 'fade-up';
  }

  get delay() {
    return parseInt(this.dataset.animateDelay || 0) / 1000;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  beforeInit() {
    if (this.paused) return;

    switch (this.type) {
      case 'fade-in':
        Motion.animate(this, { opacity: 0 }, { duration: 0 });
        break;

      case 'fade-up':
        Motion.animate(this, { transform: 'translateY(min(2rem, 90%))', opacity: 0 }, { duration: 0 });
        break;

      case 'fade-up-large':
        Motion.animate(this, { transform: 'translateY(90%)', opacity: 0 }, { duration: 0 });
        break;

      case 'zoom-out':
        Motion.animate(this, { transform: 'scale(1.3)' }, { duration: 0 });
        break;
    }
  }

  async init() {
    if (this.paused) return;
    
    switch (this.type) {
      case 'fade-in':
        await Motion.animate(this, { opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
        break;

      case 'fade-up':
        await Motion.animate(this, { transform: 'none', opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
        break;

      case 'fade-up-large':
        await Motion.animate(this, { transform: 'translateY(0)', opacity: 1 }, { duration: 1, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
        break;

      case 'zoom-out':
        await Motion.animate(this, { transform: 'scale(1)' }, { duration: 1.3, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
        break;
    }

    this.classList.add('animate');
  }

  async reset(duration) {
    switch (this.type) {
      case 'fade-in':
        await Motion.animate(this, { opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
        break;

      case 'fade-up':
        await Motion.animate(this, { transform: 'translateY(max(-2rem, -90%))', opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
        break;

      case 'fade-up-large':
        await Motion.animate(this, { transform: 'translateY(-90%)', opacity: 0 }, { duration: duration ? duration : 1, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
        break;

      case 'zoom-out':
        await Motion.animate(this, { transform: 'scale(0)' }, { duration: duration ? duration : 1.3, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
        break;
    }

    this.classList.remove('animate');
  }

  refresh() {
    this.removeAttribute('paused');
    this.beforeInit();
    this.init();
  }
}
customElements.define('animate-element', AnimateElement);

class MediaElement extends HTMLElement {
  constructor() {
    super();
  }

  get parallax() {
    return this.dataset.parallax ? parseFloat(this.dataset.parallax) : false;
  }

  get direction() {
    return this.dataset.parallaxDir || 'vertical';
  }

  get media() {
    return Array.from(this.querySelectorAll('img, video, iframe, svg, video-media, g-map'));
  }

  connectedCallback() {
    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  setupParallax() {
    const [scale, translate] = [1 + this.parallax, this.parallax * 100 / (1 + this.parallax)];

    if (this.direction === 'vertical') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateY(0)`, `scale(${scale}) translateY(${translate}%)`], transformOrigin: ['bottom', 'bottom'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else if (this.direction === 'horizontal') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateX(0)`, `scale(${scale}) translateX(${translate}%)`], transformOrigin: ['right', 'right'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(1)`, `scale(${scale})`], transformOrigin: ['center', 'center'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
  }
}
customElements.define('media-element', MediaElement);

class SplitWords extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!document.body.hasAttribute('data-title-animation')) return;

    new SplitType(this, {
      types: 'words',
      wordClass: 'single-word',
    });

    Array.from(this.querySelectorAll('.single-word')).forEach((item, index) => {
      const wrapper = document.createElement('animate-element');
      wrapper.className = 'block';
      wrapper.dataset.animate = this.dataset.animate;
      wrapper.dataset.animateDelay = parseInt(this.dataset.animateDelay || 0) + (index * 30);

      for (const itemContent of item.childNodes) {
        wrapper.appendChild(itemContent);
      }

      item.appendChild(wrapper);
    });
  }
}
customElements.define('split-words', SplitWords);

class HighlightedText extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.classList.add('animate');
  }
}
customElements.define('highlighted-text', HighlightedText, { extends: 'em' });

class MarqueeElement extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return parseInt(this.dataset.speed || 16);
  }

  get maximum() {
    return parseInt(this.dataset.maximum || Math.ceil(this.parentWidth / this.childElementWidth) + 2);
  }

  get direction() {
    return this.dataset.direction || 'left';
  }

  get parallax() {
    return !theme.config.isTouch && this.dataset.parallax ? parseFloat(this.dataset.parallax) : false;
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  init() {
    if (this.childElementCount === 1) {
      this.childElement.classList.add('animate');

      for (let index = 0; index < this.maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      this.style.setProperty('--duration', `${(33 - this.speed) * Math.min(2.5, Math.ceil(this.childElementWidth / this.parentWidth))}s`);
    }

    if (this.parallax) {
      let translate = this.parallax * 100 / (1 + this.parallax);
      if (this.direction === 'right') {
        translate = translate * -1;
      }
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      Motion.scroll(
        Motion.animate(this, { transform: [`translateX(${translate}%)`, `translateX(0)`] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      // pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.classList.remove('paused');
          }
          else {
            this.classList.add('paused');
          }
        });
      }, { rootMargin: '0px 0px 50px 0px' });
      observer.observe(this);
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('marquee-element', MarqueeElement);

class ScrolledImages extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get parallax() {
    return this.dataset.parallax ? parseFloat(this.dataset.parallax) : false;
  }

  get template() {
    return this.querySelector('template');
  }

  get images() {
    return Array.from(this.template.content.querySelectorAll('.scrolled-images__item'));
  }

  init() {
    this.beforeInit();

    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  beforeInit() {
    const main = this.querySelector('.scrolled-images__main');

    for (let i = 0; i < 3; i++) {
      let images = this.shuffle(this.images);

      if (images.length < 8) {
        let start = 0;
        while (images.length < 8) {
          images.push(images[start].cloneNode(true));
          start++;
        }
      }

      const row = document.createElement('div');
      row.classList = 'scrolled-images__row';
      images.forEach((item) => row.appendChild(item.cloneNode(true)));

      main.appendChild(row);
    }
  }

  setupParallax() {
    Array.from(this.querySelectorAll('.scrolled-images__row')).forEach((element, index) => {
      let translate = -1 * this.parallax * 100 / (1 + this.parallax);
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      if (index % 2 === 0) {
        Motion.scroll(
          Motion.animate(element, { transform: [`translateX(${translate}%)`, 'translateX(0)'] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
      else {
        Motion.scroll(
          Motion.animate(element, { transform: ['translateX(0)', `translateX(${translate}%)`] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
    });
  }

  shuffle(arr) {
    return arr.sort(() => Math.random() - 0.5);
  }
}
customElements.define('scrolled-images', ScrolledImages);

class DropdownElement extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown'
    };

    this.events = {
      afterHide: 'dropdown:afterHide',
      afterShow: 'dropdown:afterShow'
    };

    this.detectHoverListener = this.detectHover.bind(this);
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onToggleClicked.bind(this));
      button.addEventListener('mouseenter', this.detectHoverListener.bind(this));
      button.addEventListener('mouseleave', this.detectHoverListener.bind(this));
    });

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);
  }

  static get observedAttributes() {
    return ["id", 'open'];
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get container() {
    return this.querySelector('*:first-child');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));
        break;
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass);
    this.setAttribute('open', '');
    
    document.addEventListener('click', this.detectClickOutsideListener);
    document.addEventListener('keydown', this.detectEscKeyboardListener);
    document.addEventListener('focusout', this.detectFocusOutListener);

    this.afterShow();

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass);
    this.removeAttribute('open');

    document.removeEventListener('click', this.detectClickOutsideListener);
    document.removeEventListener('keydown', this.detectEscKeyboardListener);
    document.removeEventListener('focusout', this.detectFocusOutListener);

    this.afterHide();

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }

  onToggleClicked(event) {
    console.log(this);
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide();
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      this.hide();
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide();
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.show();
    }
    else {
      this.hide();
    }
  }
}
customElements.define('dropdown-element', DropdownElement);

class DropdownLocalization extends DropdownElement {
  constructor() {
    super();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
  }
}
customElements.define('dropdown-localization', DropdownLocalization);


class DropdownHelpElement extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown'
    };

    this.events = {
      afterHide: 'dropdown:afterHide',
      afterShow: 'dropdown:afterShow'
    };

    this.detectHoverListener = this.detectHover.bind(this);
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onToggleClicked.bind(this));
      button.addEventListener('mouseenter', this.detectHoverListener.bind(this));
      button.addEventListener('mouseleave', this.detectHoverListener.bind(this));
    });

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);
  }

  static get observedAttributes() {
    return ["id", 'open'];
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get container() {
    return this.querySelector('*:first-child');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));
        break;
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass);
    this.setAttribute('open', '');
    
    document.addEventListener('click', this.detectClickOutsideListener);
    document.addEventListener('keydown', this.detectEscKeyboardListener);
    document.addEventListener('focusout', this.detectFocusOutListener);

    this.afterShow();

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass);
    this.removeAttribute('open');

    document.removeEventListener('click', this.detectClickOutsideListener);
    document.removeEventListener('keydown', this.detectEscKeyboardListener);
    document.removeEventListener('focusout', this.detectFocusOutListener);

    this.afterHide();

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }

  onToggleClicked(event) {
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide();
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      this.hide();
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide();
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.show();
    }
    else {
      this.hide();
    }
  }
}
customElements.define('dropdown-help-element', DropdownHelpElement);

class DropdownHelp extends DropdownHelpElement {
  constructor() {
    super();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
  }
}
customElements.define('dropdown-help', DropdownHelp);

const lockDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu'
    };

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow'
    };

    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute('open');
    this.summaryElement.addEventListener('click', this.onSummaryClicked.bind(this));

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this));
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    return this.hasAttribute('trigger') ? this.getAttribute('trigger') : 'click';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  onSummaryClicked(event) {
    event.preventDefault();

    if (!theme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    }
    else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) + 1);
      document.body.classList.add(this.classes.bodyClass);
      
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => this.contentElement.setAttribute('open', ''), 100);
      document.addEventListener('click', this.detectClickOutsideListener);
      document.addEventListener('keydown', this.detectEscKeyboardListener);
      document.addEventListener('focusout', this.detectFocusOutListener);
      await this.transitionIn();
      this.shouldReverse();

      return theme.utils.waitForEvent(this, this.events.afterShow);
    }
    else {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) - 1);
      document.body.classList.toggle(this.classes.bodyClass, lockDropdownCount.get(DetailsDropdown) > 0);
      
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.detectClickOutsideListener);
      document.removeEventListener('keydown', this.detectEscKeyboardListener);
      document.removeEventListener('focusout', this.detectFocusOutListener);
      await this.transitionOut();
      if (!this.open) this.removeAttribute('open');

      return theme.utils.waitForEvent(this, this.events.afterHide);
    }
  }

  async transitionIn() {
    Motion.animate(this.contentElement, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1], delay: theme.config.motionReduced ? 0 : 0.2 });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(this.contentElement.firstElementChild, { transform: [`translateY(${translateY})`, 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  async transitionOut() {
    Motion.animate(this.contentElement, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(this.contentElement.firstElementChild, { transform: `translateY(${translateY})` }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger !== 'hover' || theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.open = true;
    }
    else {
      this.open = false;
    }
  }

  shouldReverse() {
    const maxWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (maxWidth > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }
}
customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
lockDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => this.open = true);
      this.addEventListener('shopify:block:deselect', () => this.open = false);
    }
  }

  async transitionIn() {
    document.body.classList.add('with-mega');
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'visible', transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0, easing: [.7, 0, .2, 1] }).finished;
  }

  async transitionOut() {
    document.body.classList.remove('with-mega');
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'hidden', transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0, easing: [.7, 0, .2, 1] }).finished;
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' });

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super();

    this.items.forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('a'));
  }

  get input() {
    return this.querySelector('input[name="locale_code"], input[name="country_code"]');
  }

  onItemClick(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.dataset.value;
    this.submit();
  }
}
customElements.define('localization-listbox', LocalizationListbox, { extends: 'form' });

class LocalizationForm extends HTMLFormElement {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    }
    else {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get select() {
    return this.querySelector('select');
  }

  beforeInit() {
    const value = this.select.options[this.select.selectedIndex].text;
    const width = theme.getElementWidth(this.select, value);
    this.select.style.setProperty('--width', `${width}px`);
  }

  init() {
    this.beforeInit();
    this.addEventListener('change', this.submit);
  }
}
customElements.define('localization-form', LocalizationForm, { extends: 'form' });

class StickyElement extends HTMLElement {
  constructor() {
    super();

    this.endScroll = window.innerHeight - this.offsetHeight - 500;
    this.currPos = window.scrollY;
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;
    this.bottomGap = 20;

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), {
      capture: true,
      passive: true,
    });
  }

  get headerHeight() {
    const header = document.querySelector('.header-section header[is="sticky-header"]');
    return header ? Math.round(header.clientHeight) + 20 : 20;
  }

  onScrollHandler() {
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;

    this.positionStickySidebar();
  }

  positionStickySidebar() {
    this.endScroll = window.innerHeight - this.offsetHeight - this.bottomGap;
    const style = window.getComputedStyle(this);
    const stickyElementTop = parseInt(style.insetBlockStart.replace('px', ''));

    if (this.stickyElementHeight + this.headerHeight + this.bottomGap > this.screenHeight) {
      if (window.scrollY < this.currPos) {
        // Scroll up
        if (stickyElementTop < this.headerHeight) {
          this.style.insetBlockStart = `${stickyElementTop + this.currPos - window.scrollY}px`;
          this.style.setProperty('--inset', `${stickyElementTop + this.currPos - window.scrollY}px`);
        }
        else if (stickyElementTop >= this.headerHeight && stickyElementTop !== this.headerHeight) {
          this.style.insetBlockStart = `${this.headerHeight}px`;
          this.style.setProperty('--inset', `${this.headerHeight}px`);
        }
      }
      else {
        // Scroll down
        if (stickyElementTop > this.endScroll) {
          this.style.insetBlockStart = `${stickyElementTop + this.currPos - window.scrollY}px`;
          this.style.setProperty('--inset', `${stickyElementTop + this.currPos - window.scrollY}px`);
        }
        else if (stickyElementTop < this.endScroll && stickyElementTop !== this.endScroll) {
          this.style.insetBlockStart = `${this.endScroll}px`;
          this.style.setProperty('--inset', `${this.endScroll}px`);
        }
      }
    }
    else {
      this.style.insetBlockStart = `${this.headerHeight}px`;
      this.style.setProperty('--inset', `${this.headerHeight}px`);
    }

    this.currPos = window.scrollY;
  }
}
customElements.define('sticky-element', StickyElement);

class ParallaxElement extends HTMLDivElement {
  constructor() {
    super();

    this.load();
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.dataset.start})`, `translateY(${this.dataset.stop})`] }),
      { target: this, offset: Motion.ScrollOffset.Any }
    );
  }
}
customElements.define('parallax-element', ParallaxElement, { extends: 'div' });

class ParallaxOverlay extends HTMLElement {
  constructor() {
    super();

    this.load();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this));
    }
  }

  refresh() {
    let options = {};
    options[this.dataset.target] = this.dataset.stop;

    Motion.animate(this, options, { duration: 0 });
  }

  load() {
    let options = {};
    options[this.dataset.target] = [this.dataset.start, this.dataset.stop];

    this.animation = Motion.scroll(
      Motion.animate(this, options),
      { target: this.parentElement, offset: Motion.ScrollOffset.Enter }
    );
  }
}
customElements.define('parallax-overlay', ParallaxOverlay);

class FooterParallax extends ParallaxElement {
  constructor() {
    super();

    document.addEventListener('matchSmall', this.unload.bind(this));
    document.addEventListener('unmatchSmall', this.load.bind(this));

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this));
    }
  }

  get mobileDisabled() {
    return true;
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: ['translateY(-50%)', 'translateY(0)'] }),
      { target: this, offset: Motion.ScrollOffset.Enter }
    );
  }

  refresh() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;
    
    setTimeout(() => {
      Motion.animate(this, { transform: 'translateY(0)' }, { duration: 0 });
    });
  }

  unload() {
    if (this.animation) {
      this.animation();
      this.style.transform = null;
    }
  }
}
customElements.define('footer-parallax', FooterParallax, { extends: 'div' });

class FooterGroup extends HTMLElement {
  constructor() {
    super();

    this.init();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.init.bind(this));
    }
  }

  get rounded() {
    return Array.from(this.querySelectorAll('.section--rounded'));
  }

  get sections() {
    return Array.from(this.querySelectorAll('.shopify-section'));
  }

  init() {
    this.sections.forEach((shopifySection, index) => {
      const section = shopifySection.querySelector('.section');
      if (section) {
        section.classList.remove('section--next-rounded');
        section.style.zIndex = this.sections.length - index;
      }
    });

    this.rounded.forEach((section) => {
      const shopifySection = section.closest('.shopify-section');
      let previousShopifySection = shopifySection.previousElementSibling;

      if (previousShopifySection === null) {
        previousShopifySection = document.querySelector('.main-content .shopify-section:last-child');
      }

      if (previousShopifySection) {
        const previousSection = previousShopifySection.querySelector('.section');
        if (previousSection) {
          previousSection.classList.add('section--next-rounded');
        }
      }
    });
  }
}
customElements.define('footer-group', FooterGroup);

class CarouselElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get watchCSS() {
    return this.hasAttribute('watch-css');
  }

  get prevNextButtons() {
    return this.hasAttribute('prev-next-buttons');
  }

  get initialIndex() {
    return parseInt(this.getAttribute('initial-index') || 0);
  }

  init() {
    if (this.items.length > 1) {
      this.carousel = new Flickity(this, {
        watchCSS: this.watchCSS,
        prevNextButtons: this.prevNextButtons,
        adaptiveHeight: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        initialIndex: this.initialIndex,
      });

      this.addEventListener('control:select', (event) => this.select(event.detail.index));
      this.carousel.on('change', this.onChange.bind(this));

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.carousel.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  select(index = 0, immediate = false) {
    if (!immediate) {
      const { selectedIndex, slides } = this.carousel;

      immediate = Math.abs(index - selectedIndex) > 3;
      
      if (index === 0 && selectedIndex === slides.length - 1) {
        immediate = false;
      }

      if (index === slides.length - 1 && selectedIndex === 0) {
        immediate = false;
      }
    }
    this.carousel.select(index, false, immediate);
  }

  onChange(index) {
    this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index } }));
  }
}
customElements.define('carousel-element', CarouselElement);

class TestimonialsElement extends CarouselElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.update(), 600);
    });
  }

  update() {
    if (this.carousel) this.carousel.select(0);
  }
}
customElements.define('testimonials-element', TestimonialsElement);

class SecondaryMedia extends HTMLElement {
  constructor() {
    super();
//this.init()
//    console.log('init');
    Motion.inView(this, () => {
      setTimeout(() => this.init());
    }, { margin: '200px 0px 200px 0px' });

    let $this = this;
    window.addEventListener("resize", function() {
      $this.reinitialize();
    });

  //  this.addEventListener('layout-change', () => this.reinitialize());
  }

  get template() {
    return this.previousElementSibling;
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  init() {
    this.appendChild(this.template.content.cloneNode(true));
    this.mediaCount = this.querySelectorAll('.media').length;

    if (window.innerWidth < 768) {
      return;
    }

    if (this.mediaCount > 1) {
      this.carousel = new Flickity(this, {
        accessibility: false,
        draggable: true,
        pageDots: false,
        prevNextButtons: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        fade: true
      });

      let progressBar = this.nextElementSibling;

      progressBar.classList.add('visible');
      setTimeout(function() {
        progressBar.classList.remove('visible');
      }, 2500);

      this.carousel.on( 'scroll', function( progress ) {
        progress = Math.max( 0, Math.min( 1, progress ) );
        progressBar.style.left = progress * 100 * 0.75 + '%';
        progressBar.classList.add('visible');
        setTimeout(function() {
          progressBar.classList.remove('visible');
        }, 2500);
      });

      if (window.innerWidth >= 768) {
        this.addEventListener('mouseleave', this.onLeaveHandler);
        this.addEventListener('mouseenter', this.onMoveHandler);
      }

      this.addEventListener('layout-change', function() {
        this.carousel.destroy();
          this.carousel = new Flickity(this, {
          accessibility: false,
          draggable: true,
          pageDots: false,
          prevNextButtons: true,
          wrapAround: true,
          rightToLeft: theme.config.rtl,
          fade: true
        });
  
        let progressBar = this.nextElementSibling;
  
        progressBar.classList.add('visible');
        setTimeout(function() {
          progressBar.classList.remove('visible');
        }, 2500);

        this.carousel.on( 'scroll', function( progress ) {
          progress = Math.max( 0, Math.min( 1, progress ) );
          progressBar.style.left = progress * 100 * 0.75 + '%';
          progressBar.classList.add('visible');
          setTimeout(function() {
            progressBar.classList.remove('visible');
          }, 2500);
        });
  
        if (window.innerWidth >= 768) {
          this.addEventListener('mouseleave', this.onLeaveHandler);
          this.addEventListener('mouseenter', this.onMoveHandler);
        }
      });
    }
  }

  reinitialize() {
    if (this.carousel) {
    this.carousel.destroy();
  }
  this.innerHTML = ''; // Clear existing content
  this.init();
  console.log('Reinitialized with DOM clearing');
  }

  onMoveHandler(event) {
    if (this.mediaCount > 1) {
      return this.carousel.select(1);
    }
  }

  onLeaveHandler() {
    this.carousel.select(0);
  }
}
customElements.define('secondary-media', SecondaryMedia);

class MotionList extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced || this.hasAttribute('motion-reduced')) return;

    this.unload();
    Motion.inView(this, this.load.bind(this));
  }

  get items() {
    return this.querySelectorAll('.card');
  }

  get itemsToShow() {
    return this.querySelectorAll('.card:not([style])');
  }

  unload() {
    Motion.animate(this.items, { y: 50, opacity: 0, visibility: 'hidden' }, { duration: 0 });
  }

  load() {
    Motion.animate(this.items, { y: [50, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5, delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) });
  }

  reload() {
    Motion.animate(this.itemsToShow, { y: [50, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5, delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) });
  }
}
customElements.define('motion-list', MotionList);

class LazyBackground extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  get image() {
    const style = window.getComputedStyle(this);
    return style.backgroundImage ? style.backgroundImage.slice(5, -2).replace('width=1', 'width=720') : false;
  }

  async init() {
    if (!this.image) return;

    const img = document.createElement('img');
    img.src = this.image;
    img.style.visibility = 'hidden';
    img.style.position = 'absolute';

    await theme.utils.imageLoaded(img);
    this.style.backgroundImage = `url(${this.image})`;
    img.remove();
  }
}
customElements.define('lazy-background', LazyBackground);

class MenuToggle extends MagnetButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.setAttribute('aria-expanded', this.expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('active');
  }
}
customElements.define('menu-toogle', MenuToggle, { extends: 'button' });

class ScrollShadow extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    }
    else {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get template() {
    return this.querySelector('template');
  }

  init() {
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(this.template.content.cloneNode(true));
    this.shadowRoot.addEventListener('slotchange', () => this.load());
    this.updater = new theme.scrollShadow.Updater(this.shadowRoot.children[1]);

    this.load();
  }

  load() {
    this.updater.on(this.children[0]);
  }

  disconnectedCallback() {
    this.updater?.on();
  }
}
customElements.define('scroll-shadow', ScrollShadow);

class CustomSelect extends HTMLSelectElement {
  constructor() {
    super();

    this.onChange();
    this.addEventListener('change', this.onChange);
  }

  onChange() {
    this.value !== '' || this.selectedIndex === -1 ? this.setAttribute('selected', '') : this.removeAttribute('selected');
  }
}
customElements.define('custom-select', CustomSelect, { extends: 'select' });

class GMap extends HTMLElement {
  constructor() {
    super();

    if (!this.dataset.apiKey || !this.dataset.mapAddress) {
      return;
    }

    Motion.inView(this, this.prepMapApi.bind(this), { margin: '600px 0px 600px 0px' });

    // Global function called by Google on auth errors.
    // Show an auto error message on all map instances.
    window.gm_authFailure = () => {

      // Show errors only to merchant in the editor.
      if (Shopify.designMode) {
        window.mapError(theme.strings.authError);
      }
    };

    window.mapError = (error, element) => {
      const container = element ? element.closest('.shopify-section') : document;
      container.querySelectorAll('.gmap--error').forEach((error) => {
        error.remove();
      });

      container.querySelectorAll('g-map').forEach((map) => {
        const message = document.createElement('div');
        message.classList.add('rte', 'alert', 'alert--error', 'gmap--error');
        message.innerHTML = error;
        map.closest('.with-map').prepend(message);
      });
    };

    window.gmNoop = () => { };
  }

  prepMapApi() {
    this.loadScript()
      .then(this.initMap.bind(this))
      .then(() => {
        setTimeout(() => {
          const container = this.closest('.banner__map');
          if (container && container.previousElementSibling) {
            container.previousElementSibling.classList.remove('opacity-0');
          }
        }, 1e3);
      });
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.dataset.apiKey}&callback=gmNoop`;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: this.dataset.mapAddress }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {

        // Show errors only to merchant in the editor.
        if (Shopify.designMode) {
          let errorMessage;

          switch (status) {
            case 'ZERO_RESULTS':
              errorMessage = theme.strings.addressNoResults;
              break;
            case 'OVER_QUERY_LIMIT':
              errorMessage = theme.strings.addressQueryLimit;
              break;
            case 'REQUEST_DENIED':
              errorMessage = theme.strings.authError;
              break;
            default:
              errorMessage = theme.strings.addressError;
              break;
          }
          window.mapError(errorMessage, this);
        }
      }
      else {
        const mapOptions = {
          zoom: parseInt(this.dataset.zoom),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        };

        const map = new google.maps.Map(this, mapOptions), center = map.getCenter();

        map.setCenter(center);

        const icon = {
          path: "M22.6746 0C10.2174 0 0 8.79169 0 21.5118C0 31.2116 4.33864 38.333 9.26606 42.998C11.7232 45.3243 14.3387 47.0534 16.6674 48.2077C18.9384 49.3333 21.1148 50 22.6746 50C24.2345 50 26.4108 49.3333 28.6818 48.2077C31.0105 47.0534 33.626 45.3243 36.0832 42.998C41.0106 38.333 45.3492 31.2116 45.3492 21.5118C45.3492 8.79169 35.1318 0 22.6746 0ZM29.6514 22.6746C29.6514 26.5278 26.5278 29.6514 22.6746 29.6514C18.8214 29.6514 15.6978 26.5278 15.6978 22.6746C15.6978 18.8214 18.8214 15.6978 22.6746 15.6978C26.5278 15.6978 29.6514 18.8214 29.6514 22.6746Z",
          fillColor: this.dataset.markerColor,
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.7
        };

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon
        });

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML));

        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');

        window.addEventListener('resize', () => {
          google.maps.event.trigger(map, 'resize');
          map.setCenter(center);
        });
      }
    });
  }
}
customElements.define('g-map', GMap);

class PreviousButton extends HoverButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:previousStatus', this.updateStatus.bind(this));
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:previous', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
          this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('previous-button', PreviousButton, { extends: 'button' });

class NextButton extends HoverButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:nextStatus', this.updateStatus.bind(this));
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:next', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
          this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('next-button', NextButton, { extends: 'button' });

class SliderElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get looping() {
    return false;
  }

  get items() {
    return this._items = this._items || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }

  get itemsToShow() {
    return Array.from(this.items).filter(element => element.clientWidth > 0);
  }

  get itemOffset() {
    if (theme.config.rtl) {
      return this.itemsToShow.length > 1 ? this.itemsToShow[0].offsetLeft - this.itemsToShow[1].offsetLeft : 0;
    }

    return this.itemsToShow.length > 1 ? this.itemsToShow[1].offsetLeft - this.itemsToShow[0].offsetLeft : 0;
  }

  get perPage() {
    let elementWidth = this.clientWidth;
    const mql = window.matchMedia('screen and (min-width: 1280px)');
    if (mql.matches) {
      const styles = window.getComputedStyle(this);
      elementWidth = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    }

    return Math.floor(elementWidth / this.itemOffset);
  }

  get totalPages() {
    return this.itemsToShow.length - this.perPage + 1;
  }

  reset() {
    this._items = Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }
  
  init() {
    this.hasPendingOnScroll = false;
    this.currentPage = 1;
    this.updateButtons();

    this.addEventListener('scroll', theme.utils.throttle(this.update.bind(this)));
    this.addEventListener('scrollend', this.scrollend);
    this.addEventListener('slider:previous', this.previous);
    this.addEventListener('slider:next', this.next);

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => event.target.scrollIntoView({behavior: 'smooth'}));
    }
  }

  previous() {
    this.scrollPosition = this.scrollLeft - this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  next() {
    this.scrollPosition = this.scrollLeft + this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  select(selectedIndex, immediate = false) {
    this.scrollPosition = this.scrollLeft - (this.currentPage - selectedIndex) * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition, immediate);
  }

  selected(selectedIndex) {
    return this.itemsToShow[selectedIndex];
  }

  scrollend() {
    this.hasPendingOnScroll = false;
    this.dispatchEventHandler();
  }

  update() {
    if (window.onscrollend === void 0) {
      clearTimeout(this.scrollendTimeout);
    }

    const previousPage = this.currentPage;
    this.currentPage = Math.round(Math.abs(this.scrollLeft) / this.itemOffset) + 1;

    if (this.currentPage !== previousPage) {
      if (!this.hasPendingOnScroll) {
        this.dispatchEventHandler();
      }

      this.itemsToShow.forEach((sliderItem, index) => {
        sliderItem.classList.toggle('selected', index + 1 === this.currentPage);
      });
    }

    if (window.onscrollend === void 0) {
      this.scrollendTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('scrollend', { bubbles: true, composed: true }));
      }, 75);
    }

    if (this.looping) return;

    this.updateButtons();
  }

  updateButtons() {
    let previousDisabled = this.currentPage === 1;
    let nextDisabled = this.currentPage === this.itemsToShow.length;

    if (this.perPage > 1) {
      previousDisabled = previousDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[0]) && this.scrollLeft === 0;
      nextDisabled = nextDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[this.itemsToShow.length - 1]);
    } 

    this.dispatchEvent(
      new CustomEvent('slider:previousStatus', {
        bubbles: true,
        detail: {
          status: previousDisabled ? (nextDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('slider:nextStatus', {
        bubbles: true,
        detail: {
          status: nextDisabled ? (previousDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
  }

  isVisible(element, offset = 0) {
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
  }

  scrollToPosition(position, immediate = false) {
    this.hasPendingOnScroll = !immediate;

    this.scrollTo({
      left: position,
      behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  dispatchEventHandler() {
    this.dispatchEvent(
      new CustomEvent('slider:change', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.itemsToShow[this.currentPage - 1],
        },
      })
    );
  }
}
customElements.define('slider-element', SliderElement);

class SliderDots extends HTMLElement {
  constructor() {
    super();

    new theme.initWhenVisible(() => {
      if (this.controlledElement) {
        this.controlledElement.addEventListener('slider:change', this.onChange.bind(this));
  
        this.items.forEach((item) => {
          item.addEventListener('click', this.onButtonClick.bind(this));
        });
      }
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get itemsToShow() {
    return Array.from(this.items).filter(element => element.clientWidth > 0);
  }

  reset() {
    this._items = Array.from(this.children);
  }

  onChange(event) {
    this.transitionTo(parseInt(event.detail.currentPage) - 1);
    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false');
    });
  }

  transitionTo(selectedIndex, immediate = false) {
    if (this.itemsToShow[selectedIndex]) {
      const scrollElement = this.hasAttribute('align-selected') ? this.closest(this.getAttribute('align-selected')) : this;
      scrollElement.scrollTo({
        left: this.itemsToShow[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.itemsToShow[selectedIndex].clientWidth / 2,
        top: this.itemsToShow[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.itemsToShow[selectedIndex].clientHeight / 2,
        behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
      });
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', item.getAttribute('data-index') === target.getAttribute('data-index') ? 'true' : 'false');
    });

    this.controlledElement.select(parseInt(target.getAttribute('data-index')));
  }
}
customElements.define('slider-dots', SliderDots);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute('style')) return;

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.style.setProperty('--progress', `${parseInt(this.dataset.value) * 0.75 * 100 / parseInt(this.dataset.max)}%`);
  }
}
customElements.define('progress-bar', ProgressBar);

const onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    if (this.posterElement) {
      this.posterElement.addEventListener('click', this.onPosterClick.bind(this));
    }

    if (this.autoplay) {
      Motion.inView(this, () => {
        if (!this.paused) {
          this.play();
        }

        return () => {
          this.pause();
        };
      });
    }
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get playing() {
    return this.hasAttribute('playing');
  }

  get player() {
    return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this.playerHandler(target, prop);
        };
      }
    });
  }

  static get observedAttributes() {
    return ['playing'];
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.playing) {
      this.paused = true;
      this.pause();
    }
    else {
      this.paused = false;
      this.play();
    }
  }

  play() {
    if (!this.playing) {
      this.player.play();
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
      }
      
      if (newValue === null) {
        return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
      }
    }
  }
}

class VideoMedia extends DeferredMedia {
  constructor() {
    super();
  }

  playerTarget() {
    if (this.hasAttribute('host')) {
     // this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      return new Promise(async (resolve) => {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 1023px)').matches;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        if (this.getAttribute('host') === 'youtube') {
          if (!window.YT || !window.YT.Player) {
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute('playing', '');
                  this.setAttribute('loaded', '');
                }
                else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute('playing');
                }
              }
            }
          });
        }
        if (this.getAttribute('host') === 'vimeo') {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = 'https://player.vimeo.com/api/player.js';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector('iframe'));
          if (muteVideo) {
            player.setMuted(true);
            this.setAttribute('muted', '');
          }

          const muteButton = this.querySelector('.mute-toggle');
          if (muteButton) {
            // Set initial label
            player.getMuted().then((muted) => {
              muteButton.textContent = muted ? 'Unmute' : 'Mute';
            });
          
            // Handle click to toggle mute
            muteButton.addEventListener('click', async () => {
              const muted = await player.getMuted();
              if (muted) {
                await player.setMuted(false);
                this.removeAttribute('muted');
                muteButton.textContent = 'Mute';
              } else {
                await player.setMuted(true);
                this.setAttribute('muted', '');
                muteButton.textContent = 'Unmute';
              }
            });
          }

          player.on('loaded', () => {
            console.log('vimeo loaded');
            let that = this;
            setTimeout(function() {             
              that.setAttribute('loaded', '');
            }, 300);
          });
          
          player.on('play', () => {
            this.setAttribute('playing', '');
            this.setAttribute('loaded', '');
          });
          player.on('pause', () => this.removeAttribute('playing'));
          player.on('ended', () => this.removeAttribute('playing'));
          resolve(player);
        }
      });
    }
    else {
      this.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
     // this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      const player = this.querySelector('video');

      player.addEventListener('loadeddata', () => {
        console.log('mp4 loaded');
        this.setAttribute('loaded', '');
      });

      player.addEventListener('play', () => {
        this.setAttribute('playing', '');
        this.removeAttribute('suspended');
      });
      player.addEventListener('pause', () => {
        if (!player.seeking && player.paused) {
          this.removeAttribute('playing');
        }
      });
      return player;
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute('host') === 'youtube') {
      prop === 'play' ? target.playVideo() : target.pauseVideo();
    }
    else {
      if (prop === 'play' && !this.hasAttribute('host')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '');
            target.controls = true;
            const replacementImageSrc = target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      }
      else {
        target[prop]();
      }
    }
  }
}
customElements.define('video-media', VideoMedia);

class ModelMedia extends DeferredMedia {
  constructor() {
    super();

    this.player;

    if (this.closeElement) {
      this.closeElement.addEventListener('click', this.onCloseClick.bind(this));
    }
  }

  get closeElement() {
    return this.querySelector('[id^="DeferredPosterClose-"]');
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.modelViewerUI) {
      this.modelViewerUI.pause();
    }
  }

  playerTarget() {
    return new Promise(() => {
      this.setAttribute('loaded', '');

      Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: this.setupShopifyXR.bind(this),
        },
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    });
  }

  playerHandler(target, prop) {
    target[prop]();
  }

  async setupShopifyXR() {
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', this.setupShopifyXR.bind(this));
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    const modelViewer = this.querySelector('model-viewer');
    if (modelViewer && !modelViewer.hasAttribute('loaded')) {
      modelViewer.setAttribute('loaded', '');
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.modelViewerPlayed.bind(this));
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.modelViewerPaused.bind(this));

      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    }
  }

  modelViewerPlayed() {
    this.setAttribute('playing', '');
    this.closeElement.removeAttribute('hidden');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:play', { bubbles: true }));
  }

  modelViewerPaused() {
    this.removeAttribute('playing');
    this.closeElement.setAttribute('hidden', '');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:pause', { bubbles: true }));
  }
}
customElements.define('model-media', ModelMedia);

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', this.onVariantChange);

 //   this.updateOptions();
 //   this.updateMasterId();
 //   this.setAvailability();
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  onVariantChange(event) {

    const fieldsets = Array.from(this.querySelectorAll('fieldset'));

    const uncheckedFieldsets = fieldsets.filter(fieldset => !fieldset.querySelector('input[type="radio"]:checked') && fieldset.dataset.optionName !== "Color").map(fieldset => fieldset.dataset.optionName);  


    if (uncheckedFieldsets.length > 0) {
      
      const variants = this.getVariantData(); // Fetch variant data once
      uncheckedFieldsets.forEach(optionName => {
        const fieldset = document.querySelector(`fieldset[data-option-name="${optionName}"]`);
        const isOption1 = fieldset.dataset.optionIndex === 'option1';   
        fieldset.querySelectorAll('input[type="radio"]').forEach(input => {
          const option1 = isOption1 ? input.value : event.target.value;
          const option2 = isOption1 ? event.target.value : input.value;
          if (!variants.some(variant => variant.option1 === option1 && variant.option2 === option2 && variant.available)) {
            input.classList.add('disabled');
          }
        });
      }); 
      const formattedOptions = uncheckedFieldsets.join(" and ");
      const defaultATC = document.querySelector('.product-form__submit .btn-text');
      const stickyATC = document.querySelector('.sticky-atc');
      if (defaultATC != null) {
        defaultATC.innerHTML = `Select ${formattedOptions}`;
      }
      if (stickyATC != null) {
        stickyATC.innerHTML = `Select ${formattedOptions}`;

        const variantTitleDivs = document.querySelectorAll('sticky-variant-title div');
        variantTitleDivs.forEach(div => {
          const option = div.dataset.option;      
          if (!uncheckedFieldsets.includes(option)) {
            div.classList.remove('hidden');     
            const checkedInput = document.querySelector(`.product-form__input[data-option-name="${option}"] input:checked`);
            console.log(checkedInput);
            const stickyOptionValue = checkedInput ? checkedInput.value : '';       
            const stickyOptionValueEl = div.querySelector('span.color-light');
            if (stickyOptionValueEl) {
              stickyOptionValueEl.textContent = stickyOptionValue;
            }
          }
        });
        
      }      
    } else {
      
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
          
      this.updateOptions();
      this.updateMasterId();
      this.toggleBuyButton(true, '', false);
      this.updatePickupAvailability();
      this.removeErrorMessage();
  
      if (!this.currentVariant) {
        this.toggleBuyButton(true, '', true);
        this.setUnavailable();
        this.setAvailabilityInUnavailable();
      }
      else {
        this.updateURL();
        this.updateVariantInput();
        this.updateOptionValue();
        this.renderProductInfo();
        this.setAvailability();
  
        (this.productForm ?? document).dispatchEvent(new CustomEvent('variant:change', {
          detail: {
            variant: this.currentVariant
          }
        }));
      }
      
    }
    
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData()?.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  getSlug(someString) {
    return someString.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#ProductForm-${this.dataset.section}, #ProductFormInstallment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.setAttribute('hidden', '');
      pickUpAvailability.innerHTML = '';
    }
  }

  updateOptionValue() {
    for (let index = 1; index <= 3; index++) {
      const option = `option${index}`;
      const id = `${this.dataset.section}-${option}`;
      const destination = document.getElementById(id);
      const source = this.currentVariant[option];

      if (source && destination) destination.innerHTML = source;
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('form[is="product-form"]');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        this.updateSKU(responseText);
        this.updatePrice(responseText);
        this.updateStickyPrice(responseText);
        this.updateInventoryStatus(responseText);
        if (this.currentVariant) this.toggleBuyButton(!this.currentVariant.available, theme.variantStrings.soldOut);

        document.dispatchEvent(new CustomEvent('productInfo:loaded'));
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateSKU(responseText) {
    const id = `Sku-${this.dataset.section}`;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = document.getElementById(id);
    const source = parsedHTML.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
    if (destination) destination.classList.remove('invisible');
  }

  updatePrice(responseText) {
    const id = `Price-${this.dataset.section}`;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = document.getElementById(id);
    const source = parsedHTML.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
    if (destination) destination.classList.remove('invisible');
  }

  updateStickyPrice(responseText) {
    const id = `StickyPrice-${this.dataset.section}`;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = document.getElementById(id);
    const source = parsedHTML.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
    if (destination) destination.classList.remove('invisible');
  }

  updateInventoryStatus(responseText) {
    const id = `Inventory-${this.dataset.section}`;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = document.getElementById(id);
    const source = parsedHTML.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
    if (destination) destination.classList.remove('invisible');
  }

  toggleBuyButton(disable = true, text, modifyClass = true) {
    const productForms = document.querySelectorAll(`#ProductForm-${this.dataset.section}, #StickyProductForm-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const buyButton = productForm.querySelector('[name="add"]');
      const buyButtonText = productForm.querySelector('[name="add"] > .btn-text');
      const buyButtonTextChild = productForm.querySelector('[name="add"] > .btn-text span');
      const notifyMeBtn = productForm.querySelector('#notify-me-btn');
      if (!buyButton) return;

      console.log(text);

      if (disable) {
        buyButton.setAttribute('disabled', '');
        if(!buyButton.parentNode.classList.contains('bis-hidden')) {
     //     buyButton.parentNode.classList.add('out-of-stock');
          if(notifyMeBtn && text == theme.variantStrings.soldOut) {
            buyButton.parentNode.classList.add('out-of-stock');
            notifyMeBtn.style.display = 'block';
          }
        }
        if (text) {
          (buyButtonTextChild || buyButtonText).innerText = text;
        }
      }
      else {
        buyButton.removeAttribute('disabled');   
        buyButton.parentNode.classList.remove('out-of-stock');
        if(notifyMeBtn) {
          notifyMeBtn.style.display = 'none';
        }
        (buyButtonTextChild || buyButtonText).innerText = buyButton.dataset.preOrder === 'true' ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
      }

      const stickyATC = document.querySelector('.sticky-atc');
      if (stickyATC != null) {
        stickyATC.innerHTML = document.querySelector('.product-form__submit').innerText;
      }
      
      if (!modifyClass) return;
    });
  }

  setUnavailable() {
    const productForms = document.querySelectorAll(`#ProductForm-${this.dataset.section}`, `#StickyProductForm-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const buyButton = productForm.querySelector('[name="add"]');
      const buyButtonText = buyButton.querySelector('[name="add"] > .btn-text');
      const buyButtonTextChild = productForm.querySelector('[name="add"] > .btn-text span');
      if (!buyButton) return;
      (buyButtonTextChild || buyButtonText).innerText = theme.variantStrings.unavailable;

      const price = document.getElementById(`Price-${this.dataset.section}`);
      if (price) price.classList.add('invisible');

      const stickyPrice = document.getElementById(`StickyPrice-${this.dataset.section}`);
      if (stickyPrice) stickyPrice.classList.add('invisible');

      const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
      if (inventory) inventory.classList.add('invisible');
    });
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  setAvailability() {
    if (!this.currentVariant) return;

    this.querySelectorAll('.variant-input-wrapper').forEach(group => {
      this.disableVariantGroup(group);
    });

    const currentlySelectedValues = this.currentVariant.options.map((value, index) => { return { value, index: `option${index + 1}` } })
    const initialOptions = this.createAvailableOptionsTree(this.variantData, currentlySelectedValues, this.currentVariant);

    for (var [option, values] of Object.entries(initialOptions)) {
      this.manageOptionState(option, values);
    }
  }

  setAvailabilityInUnavailable() {
    this.querySelectorAll('.variant-input-wrapper').forEach(group => {
      this.disableVariantGroup(group);
    });

    const currentlySelectedValues = this.options.map((value, index) => { return { value, index: `option${index + 1}` } })
    const initialOptions = this.createAvailableOptionsTree(this.variantData, currentlySelectedValues, this.currentVariant);

    for (var [option, values] of Object.entries(initialOptions)) {
      this.manageOptionState(option, values);
    }
  }

  enableVariantOption(group, obj) {
    const value = obj.value.replace(/([ #;&,.+*~\':"!^$[\]()=>|\/@])/g, '\\$1');
    group.querySelector('option[value="' + value + '"]').disabled = false;
  }

  disableVariantGroup(group) {
    group.querySelectorAll('option').forEach((option) => {
      option.disabled = true;
    });
  }

  manageOptionState(option, values) {
    const group = this.querySelector('.variant-input-wrapper[data-option-index="' + option + '"]');

    // Loop through each option value
    values.forEach(obj => {
      this.enableVariantOption(group, obj);
    });
  }

  createAvailableOptionsTree(variants, currentlySelectedValues) {
    // Reduce variant array into option availability tree
    return variants.reduce((options, variant) => {

      // Check each option group (e.g. option1, option2, option3) of the variant
      Object.keys(options).forEach(index => {

        if (variant[index] === null) return;

        let entry = options[index].find(option => option.value === variant[index]);

        if (typeof entry === 'undefined') {
          // If option has yet to be added to the options tree, add it
          entry = { value: variant[index], soldOut: true }
          options[index].push(entry);
        }

        const currentOption1 = currentlySelectedValues.find(({ value, index }) => index === 'option1')
        const currentOption2 = currentlySelectedValues.find(({ value, index }) => index === 'option2')

        switch (index) {
          case 'option1':
            // Option1 inputs should always remain enabled based on all available variants
            entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            break;
          case 'option2':
            // Option2 inputs should remain enabled based on available variants that match first option group
            if (currentOption1 && variant['option1'] === currentOption1.value) {
              entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            }
          case 'option3':
            // Option 3 inputs should remain enabled based on available variants that match first and second option group
            if (
              currentOption1 && variant['option1'] === currentOption1.value
              && currentOption2 && variant['option2'] === currentOption2.value
            ) {
              entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            }
        }
      })

      return options;
    }, { option1: [], option2: [], option3: [] })
  }
}
customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  enableVariantOption(group, obj) {
    const value = obj.value.replace(/([ #;&,.+*~\':"!^$[\]()=>|\/@])/g, '\\$1');
    const input = group.querySelector('input[data-option-value="' + value + '"]');

    // Variant exists - enable & show variant
    input.classList.remove('disabled');

    // Variant sold out - cross out option (remains selectable)
    if (obj.soldOut) {
      input.classList.add('disabled');
    }
  }

  disableVariantGroup(group) {
    group.querySelectorAll('input').forEach((input) => {
      input.classList.add('disabled');
    });
  }
}
customElements.define('variant-radios', VariantRadios);

class ProductForm extends HTMLFormElement {
  constructor() {
    super();

    this.querySelector('[name=id]').disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  get submitButtons() {
    return this._submitButtons = this._submitButtons || document.querySelectorAll(`[type="submit"][form="${this.getAttribute('id')}"]`);
  }

  get hideErrors() {
    return this.dataset.hideErrors === 'true';
  }

  onSubmitHandler(event) {
    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') return;
    
    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
    
    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this);
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message,
          });
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));
          
          const submitButtonText = this.submitButton.querySelector('.btn-text span');
          if (!submitButtonText || !submitButtonText.hasAttribute('data-sold-out')) return;
          submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.error = true;
          return;
        }

        const cartJson = await (await fetch(`${theme.routes.cart_url}`, { ...theme.utils.fetchConfig()})).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        const quickViewModal = this.closest('quick-view');
        if (quickViewModal) {
          document.body.addEventListener(
            'modal:afterHide',
            () => {
              setTimeout(() => {
                this.cartDrawer?.show(this.activeElement);
              });
            },
            { once: true }
          );
          quickViewModal.hide(true);
        }
        else {
          this.cartDrawer?.show(this.activeElement);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButtons.forEach(submitButton => {
          submitButton.removeAttribute('aria-busy');
          submitButton.querySelector('.btn-text').innerText = 'Added'
          setTimeout(function() {
            submitButton.querySelector('.btn-text').innerText = 'Add to Cart'
          }, 3000)
        });

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-disabled'));
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;
    
    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }
}
customElements.define('product-form', ProductForm, { extends: 'form' });

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.pauseAllMedia(), 500);
    });

    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChanged.bind(this));

    this.addEventListener('lightbox:open', (event) => this.openZoom(event.detail.index));
    this.sliderGallery.addEventListener('slider:change', this.onSlideChange.bind(this));

    this.countMediaGallery();
  }

  get viewInSpaceButton() {
    return this.querySelector('[data-shopify-xr]');
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get sliderGallery() {
    return this.querySelector('slider-element');
  }

  get sliderDots() {
    return this.querySelector('media-dots');
  }

  get mediaPreview() {
    return this.querySelector('.product__preview .product__media');
  }

  get hideVariants() {
    return this._hideVariants = this._hideVariants || this.querySelectorAll('.product__media--variant').length > 0;
  }

  get gangOption() {
    if (this._gangOption) return this._gangOption;
    const mediaItemWithGang = this.sliderGallery.querySelector('[data-gang-option]');
    return mediaItemWithGang ? this._gangOption = mediaItemWithGang.dataset.gangOption : false;
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe;

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(theme.settings.pswpModule),

      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,

      // Recommended options for this plugin
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: false,
      returnFocus: true,
      zoom: false,
    });

    lightbox.addFilter('thumbEl', (_thumbEl, data) => data['thumbnailElement']);

    lightbox.on('contentLoad', (event) => {
      const { content } = event;

      console.log(event);

      if (content.type === 'video' || content.type === 'external_video' || content.type === 'model') {
        event.preventDefault();

        // Create a container for video and assign it to the `content.element` property
        content.element = document.createElement('div');
        content.element.className = 'pswp__video-container';
        content.element.appendChild(content.data.domElement.cloneNode(true));
      }
    });

    lightbox.on('change', () => {
      this.sliderGallery.select(lightbox.pswp.currIndex + 1, true);
    });

    lightbox.on('close', () => {
      const slideSelected = this.sliderGallery.selected(lightbox.pswp.currIndex);
      lightbox.pswp._lastActiveElement = slideSelected ? slideSelected.querySelector('button') : document.activeElement;
    });

    lightbox.init();
    return this._photoswipe = lightbox;
  }

  onVariantChanged(event) {
    const currentVariant = event.detail.variant;
    if (!currentVariant.featured_media) return;

    const newMedia = this.sliderGallery.querySelector(`[data-media-id="${currentVariant.featured_media.id}"]`);
    if (newMedia === null) return;

    if (this.gangOption) {
      this.sliderGallery.items.forEach((item) => item.hidden = item.dataset.gangConnect !== newMedia.dataset.gangConnect);
      this.sliderGallery.reset();

      if (this.sliderDots) {
        this.sliderDots.items.forEach((item) => item.hidden = item.dataset.gangConnect !== newMedia.dataset.gangConnect);
        this.sliderDots.reset();
        this.sliderDots.resetIndexes();
        this.sliderDots.transitionTo(1, true);
      }
    }

    this.setActiveMedia(currentVariant.featured_media.id, this.hideVariants);

    if (this.mediaPreview) {
      this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => media.classList.remove('xl:hidden'));
      this.mediaPreview.parentNode.replaceChild(newMedia.cloneNode(true), this.mediaPreview);
      newMedia.classList.add('xl:hidden');
    }

    this.countMediaGallery();
  }

  onSlideChange(event) {
    const activeMedia = event.detail.currentElement;
    this.playActiveMedia(activeMedia);

    if (this.viewInSpaceButton) {
      if (activeMedia.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-media-id'));
      } else {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'));
      }
    }
  }

  setActiveMedia(mediaId, prepend) {
    const activeMedia = this.sliderGallery.querySelector(`[data-media-id="${mediaId}"]`);

    if (prepend) {
      activeMedia.parentElement.prepend(activeMedia);
      this.sliderGallery.reset();

      if (this.sliderDots) {
        const activeThumbnail = this.sliderDots.querySelector(`[data-media-id="${mediaId}"]`);
        activeThumbnail.parentElement.prepend(activeThumbnail);
        this.sliderDots.reset();
        this.sliderDots.resetIndexes();
      }
    }
    else {
      this.sliderGallery.select(this.sliderGallery.itemsToShow.indexOf(activeMedia) + 1, true);
    }

    if (this.gangOption) {
      this.sliderGallery.select(1, true);
    }

 /*   if (theme.config.mqlSmall) {
      const quickViewModal = this.closest('quick-view');
      if (quickViewModal) {
        quickViewModal.querySelector('.quick-view__content').scrollTo({
          top: activeMedia.getBoundingClientRect().top,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        });
      }
      else {
        window.scrollTo({
          top: activeMedia.getBoundingClientRect().top + window.scrollY - 95,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        });
      }
    }*/
  }

  playActiveMedia(activeMedia) {
    if (typeof activeMedia === 'undefined') return;
    
    const deferredMedia = activeMedia.querySelector('.deferred-media');

    this.sliderGallery.querySelectorAll('.deferred-media').forEach((media) => {
      deferredMedia === media ? media.play() : media.pause();
    });
  }

  pauseAllMedia() {
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media, index) => {
      if (index > 0) {
        const deferredMedia = media.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.pause === 'function') deferredMedia.pause();
      }
    });
  }

  openZoom(index = 0) {
    let dataSource = this.sliderGallery.itemsToShow.map((media) => {
      const image = media.querySelector('img');

      if (media.dataset.mediaType === 'image') {
        return {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        };
      }
      
      if (media.dataset.mediaType === 'video' || media.dataset.mediaType === 'external_video' || media.dataset.mediaType === 'model') {
        const video = media.querySelector('.deferred-media');
        return {
          thumbnailElement: image,
          domElement: video,
          type: media.dataset.mediaType,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: 800,
          height: 800 / video.dataset.aspectRatio,
          alt: image.alt,
          thumbCropped: true
        };
      }
    });
    
    if (this.mediaPreview && this.mediaPreview.offsetParent) {
      if (this.mediaPreview.dataset.mediaType === 'image') {
        const image = this.mediaPreview.querySelector('img');
        dataSource.push({
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        });

        if (index === -1) {
          index = dataSource.length - 1;
        }
      }
    }

    this.photoswipe.loadAndOpen(index, dataSource);
  }

  countMediaGallery() {
    let mediaCount = 0;
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => {
      if (!media.classList.contains('xl:hidden') && !media.hasAttribute('hidden')) {
        mediaCount++;
      }
    });

    if (this.mediaPreview) {
      mediaCount++;
    }

    mediaCount > 1 ? this.classList.remove('with-only1') : this.classList.add('with-only1');
  }
}
customElements.define('media-gallery', MediaGallery);

class MediaLightboxButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onButtonClick);
  }

  onButtonClick() {
    const media = this.closest('[data-media-id]');
    const sliderGallery = this.closest('slider-element');
    const openIndex = sliderGallery ? sliderGallery.itemsToShow.indexOf(media) : -1;

    this.dispatchEvent(new CustomEvent('lightbox:open', {
      bubbles: true,
      detail: {
        index: openIndex
      }
    }));
  }
}
customElements.define('media-lightbox-button', MediaLightboxButton, { extends: 'button' });

class MediaHoverButton extends MediaLightboxButton {
  constructor() {
    super();
  }

  get zoomRatio() {
    return 2;
  }

  onButtonClick(event) {
    if (theme.config.isTouch) {
      super.onButtonClick();
    }
    else {
      const media = this.closest('[data-media-type="image"]');
      if (media) {
        const image = media.querySelector('img');
        this.gallery = this.closest('slider-element');
  
        this.magnify(image);
        this.moveWithHover(image, event);
      }
    }
  }

  createOverlay(image) {
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${image.src}`);
    const overlay = document.createElement('media-hover-overlay');
    this.prepareOverlay(overlay, overlayImage);
  
    this.toggleLoadingSpinner(image);
  
    overlayImage.onload = () => {
      this.toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
    };

    if (this.gallery) this.gallery.classList.add('magnify');
  
    return overlay;
  }

  prepareOverlay(container, image) {
    container.setAttribute('class', 'media z-10 absolute top-0 left-0 w-full h-full');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
    container.style.cursor = 'zoom-out';
  }
  
  toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement;
    loadingSpinner.classList.toggle('loading');
    loadingSpinner.classList.toggle('pointer-events-none');
  }

  moveWithHover(image, event) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    this.overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    this.overlay.style.backgroundSize = `${image.width * this.zoomRatio}px`;
  }

  magnify(image) {
    this.overlay = this.createOverlay(image);
    this.overlay.onclick = () => this.reset();
    this.overlay.onmousemove = (event) => this.moveWithHover(image, event);
    this.overlay.onmouseleave = () => this.reset();
  }

  reset() {
    this.overlay.remove();

    if (this.gallery) this.gallery.classList.remove('magnify');
  }
}
customElements.define('media-hover-button', MediaHoverButton, { extends: 'button' });

class MediaDots extends SliderDots {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.resetIndexes.bind(this));
    }
    else {
      Motion.inView(this, this.resetIndexes.bind(this));
    }
  }

  resetIndexes() {
    let newIndex = 1;

    this.itemsToShow.forEach((item, index) => {
      item.setAttribute('data-index', newIndex);
      item.setAttribute('aria-current', index === 0 ? 'true' : 'false');

      newIndex++;
    });
  }
}
customElements.define('media-dots', MediaDots);

class XModal extends ModalElement {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('x-modal', XModal);

class LogoList extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return parseInt(this.dataset.speed || 16);
  }

  get maximum() {
    return parseInt(this.dataset.maximum || Math.ceil(this.parentWidth / this.childElementWidth) + 2);
  }

  get direction() {
    return this.dataset.direction || 'left';
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  disconnectedCallback() {
    if (this.marquee) {
      this.pause();
      this.marquee.destroy();
    }
  }

  init() {
    if (this.childElementCount === 1) {
      this.childElement.classList.add('animate');
      
      for (let index = 0; index < this.maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      if (theme.config.isTouch) {
        this.style.setProperty('--duration', `${33 - this.speed}s`);
      }
      else {
        this.marquee = new Flickity(this, {
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          freeScroll: true,
          rightToLeft: this.direction === 'right',
        });
  
        // Set initial position to be 0
        this.marquee.x = 0;
  
        // Start the marquee animation
        this.play();
  
        this.addEventListener('mouseenter', this.pause);
        this.addEventListener('mouseleave', this.play);
      }
    }
  }

  play() {
    // Set the decrement of position x
    this.marquee.x -= this.speed * 0.1;

    // Settle position into the slider
    this.marquee.settle(this.marquee.x);

    // Set the requestId to the local variable
    this.requestId = requestAnimationFrame(this.play.bind(this));
  }

  pause() {
    if (this.requestId) {
      // Cancel the animation
      cancelAnimationFrame(this.requestId);

      // Reset the requestId for the next animation.
      this.requestId = undefined;
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('logo-list', LogoList);

class TextScrolling extends HTMLElement {
  constructor() {
    super();

    this.items.forEach((item) => {
      const header = item.querySelector('.heading');

      Motion.scroll(
        Motion.animate(header, { opacity: [0, 0, 1, 1, 1, 0, 0] }),
        { target: header, offsets: ['33vh', '66vh'] }
      );
    });
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }
}
customElements.define('text-scrolling', TextScrolling);

class TabsElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => button.addEventListener('click', () => this.selectedIndex = index));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
  }

  get buttons() {
    return this._buttons = this._buttons || Array.from(this.querySelectorAll('button[role="tab"]'));
  }

  get indicators() {
    return this._indicators = this._indicators || Array.from(this.querySelectorAll('.indicators'));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('button--primary', index === parseInt(newValue));
      button.classList.toggle('button--secondary', index !== parseInt(newValue));
      button.disabled = index === parseInt(newValue);
    });

    this.indicators.forEach((button, index) => {
      button.hidden = index !== parseInt(newValue);
    });

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromButton = this.buttons[parseInt(oldValue)];
      const toButton = this.buttons[parseInt(newValue)];
      this.transition(document.getElementById(fromButton.getAttribute('aria-controls')), document.getElementById(toButton.getAttribute('aria-controls')));
    }
  }

  async transition(fromPanel, toPanel) {
    await Motion.animate(fromPanel, { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] }, { duration: 0.15 }).finished;
    
    fromPanel.hidden = true;
    toPanel.hidden = false;
    
    Motion.animate(toPanel, { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: 0.15 }).finished;
    toPanel.querySelector('motion-list')?.load();
  }
}
customElements.define('tabs-element', TabsElement);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get date() {
    return this._date = this._date || new Date(`${this.dataset.month}/${this.dataset.day}/${this.dataset.year} ${this.dataset.hour}:${this.dataset.minute}:00 -04:00`);
  }

  get isCompact() {
    return this.dataset.compact === 'true' || (this.dataset.compact === 'mobile' && theme.config.mqlSmall);
  }

  init() {
    this.calculate();
    this.timerInterval = setInterval(this.calculate.bind(this), 1000);
  }

  calculate() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);
    if (timeDifference < 0) {
      this.complete();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor(timeDifference / (secondsInADay) * 1);
    const hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    if (this.isCompact) {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}${theme.dateStrings.d}</p></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}${theme.dateStrings.h}</p></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}${theme.dateStrings.m}</p></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}${theme.dateStrings.s}</p></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
    else {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}</p><span>${days === 1 ? theme.dateStrings.day : theme.dateStrings.days}</span></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}</p><span>${hours === 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</span></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}</p><span>${mins === 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</span></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}</p><span>${secs === 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</span></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  complete() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.hidden = true;
  }
}
customElements.define('countdown-timer', CountdownTimer);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    this.isInitialized = false;

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get button() {
    return this.querySelector('button');
  }

  get bounding() {
    return this.getBoundingClientRect();
  }

  get horizontal() {
    return this.dataset.layout === 'horizontal';
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  init() {
    if (this.isInitialized) return; // Avoid double initialization

    this.isInitialized = true;
    this.active = false;

    this.button.addEventListener('touchstart', this.startHandler.bind(this), theme.supportsPassive ? { passive: true } : false);
    document.body.addEventListener('touchend', this.endHandler.bind(this), theme.supportsPassive ? { passive: true } : false);
    document.body.addEventListener('touchmove', this.onHandler.bind(this), theme.supportsPassive ? { passive: true } : false);

    this.button.addEventListener('mousedown', this.startHandler.bind(this));
    document.body.addEventListener('mouseup', this.endHandler.bind(this));
    document.body.addEventListener('mousemove', this.onHandler.bind(this));

    setTimeout(() => this.animate(), 1e3);
  }

  reinit() {
    // Reset the state to trigger functionality again
    this.isInitialized = false;
    this.init();
  }

  animate() {
    this.setAttribute('animate', '');

    this.classList.add('animating');
    setTimeout(() => {
      this.classList.remove('animating');
    }, 1e3);
  }

  startHandler() {
    this.active = true;
    this.classList.add('scrolling');
  }

  endHandler() {
    this.active = false;
    this.classList.remove('scrolling');
  }

  onHandler(e) {
    if (!this.active) return;
    
    const event = (e.touches && e.touches[0]) || e;
    let x = this.horizontal
                ? event.pageX - (this.bounding.left + window.scrollX)
                : event.pageY - (this.bounding.top + window.scrollY);
                
    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;
    
    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, (Math.min(x, max)));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', mousePercent + '%');
  }
}
customElements.define('image-comparison', ImageComparison);

class LookbookElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('.hotspot'));
  }

  init() {
    this.items.forEach((item) => item.addEventListener('mouseenter', (event) => this.select(this.items.indexOf(event.target))));

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', this.animate.bind(this));
      section.addEventListener('shopify:section:deselect', this.closeAll.bind(this));
      this.addEventListener('shopify:block:select', (event) => this.open(this.items.indexOf(event.target)));
    }

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.openAll();

    setTimeout(() => this.closeAll(), 3e3);
  }

  open(selectedIndex) {
    this.items.forEach((item, index) => item.classList.toggle('active', selectedIndex === index));
  }

  openAll() {
    this.items.forEach((item) => item.classList.add('active'));
  }

  closeAll() {
    this.items.forEach((item) => item.classList.remove('active'));
  }

  select(selectedIndex) {
    this.items.forEach((item, index) => item.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false'));
    this.dispatchEvent(new CustomEvent('lookbook:change', { bubbles: true, detail: { index: selectedIndex } }));
  }
}
customElements.define('lookbook-element', LookbookElement);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    this.lookbook.addEventListener('lookbook:change', (event) => this.carousel.select(event.detail.index));
    this.carousel.addEventListener('carousel:change', (event) => this.lookbook.select(event.detail.index));
  }

  get lookbook() {
    return this.querySelector('lookbook-element');
  }

  get carousel() {
    return this.querySelector('carousel-element');
  }
}
customElements.define('shop-the-look', ShopTheLook);

class SpinningText extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    }
    else {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
  }

  get string() {
    let string = this.dataset.string;
    string = string.replace(/  +/g, ' ');
    return '•' + string.replace(/ +/g, '•')
  }

  init() {
    const canTrig = CSS.supports('(top: calc(sin(1) * 1px))');
    const OPTIONS = {
      TEXT: this.string,
      SIZE: 2,
      SPACING: 2,
    };

    const HEADING = document.createElement('div');
    const text = OPTIONS.TEXT;

    // Take the text and split it into spans...
    const chars = text.split('');
    this.style.setProperty('--char-count', chars.length);

    for (let c = 0; c < chars.length; c++) {
      HEADING.innerHTML += `<span aria-hidden="true" class="split-char" style="--char-index: ${c};">${chars[c]}</span>`;
    }
    HEADING.innerHTML += `<span class="sr-only">${OPTIONS.TEXT}</span>`;
    HEADING.classList = 'split-chars';

    // Set the styles
    this.style.setProperty('--font-size', OPTIONS.SIZE);
    this.style.setProperty('--character-width', OPTIONS.SPACING);
    this.style.setProperty(
      '--radius',
      canTrig
        ? 'calc((var(--character-width) / sin(var(--inner-angle))) * -1ch)'
        : `calc(
        (${OPTIONS.SPACING} / ${Math.sin(
            360 / this.children.length / (180 / Math.PI)
          )})
        * -1ch
      )`
    );

    // Append
    this.appendChild(HEADING);
  }
}
customElements.define('spinning-text', SpinningText);

class SlideshowElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    }
    else {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get draggable() {
    return this.hasAttribute('draggable');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  init() {
    const that = this;
    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        pageDots: false,
        prevNextButtons: false,
        fade: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: false,
        draggable: this.draggable ? '>1' : false,
        on: {
          ready: function() {
            const { selectedElement } = this;
            that.onReady(selectedElement);
          }
        }
      });
      const slideDurations = Array.from(this.querySelectorAll('.banner')).map(cell => parseInt(cell.getAttribute('data-duration'), 10));
      let currentSlideIndex = 0;
      let autoplayTimer = null;
      let remainingTime = null;
      let isPaused = false;
      let startTime = null;

      if (this.autoplay) {
        function startAutoplay() { 
          that.classList.remove('slideshow-paused');
          clearTimeout(autoplayTimer); 
          const duration = remainingTime !== null ? remainingTime : slideDurations[currentSlideIndex];
          startTime = Date.now();   
          autoplayTimer = setTimeout(() => {
            that.slider.next(); 
            currentSlideIndex = that.slider.selectedIndex;
            remainingTime = null;
            startAutoplay();
          }, duration); 
          isPaused = false; 
        }
  
        function stopAutoplay() {
          that.classList.add('slideshow-paused');
          if (!isPaused) {
            clearTimeout(autoplayTimer);
            if (startTime) {
              const elapsed = Date.now() - startTime;
              remainingTime = slideDurations[currentSlideIndex] - elapsed;
            }   
            isPaused = true;
          }
        }
     
        this.slider.on("change", (index) => {
          currentSlideIndex = index;
          remainingTime = null;
          startAutoplay();
        });
        
        that.addEventListener("mouseenter", stopAutoplay); 
        that.addEventListener("mouseleave", startAutoplay);
        startAutoplay(); 
      }

      this.slider.on('change', this.onChange.bind(this));
      this.addEventListener('slider:previous', () => this.slider.previous());
      this.addEventListener('slider:next', () => this.slider.next());
      this.addEventListener('slider:play', () => this.slider.playPlayer());
      this.addEventListener('slider:pause', () => this.slider.pausePlayer());
  
      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
      }
    }
    else if (this.items.length === 1) {
      const selectedElement = this.firstChild;
      that.onReady(selectedElement);
    }

    that.items.forEach(item => {
      let doubleImageNav = item.querySelectorAll('.banner__double-image .banner__double-image-navigation div');
      if (doubleImageNav.length > 0) {
        doubleImageNav.forEach(nav => {
          nav.addEventListener('click', function() {
            nav.parentElement.children.forEach(sib => sib.classList.remove('active'));
            nav.classList.add('active')
            let index = parseInt(nav.dataset.index);
            let side = nav.dataset.side;
            item.querySelectorAll(`.banner__double-image picture[data-index="${index}"]`).forEach(image => {
              if(image.dataset.side == side) {
                image.classList.add('active');
              } else { 
                image.classList.remove('active');
              }
            });
            item.closest('.slideshow-container').children.forEach(sib => {    
              if (sib.classList.contains('slideshow-content')) {
                sib.querySelectorAll('.slideshow-word').forEach(word => {
                  console.log(parseInt(word.dataset.index) + ' ' + index);
                  if(parseInt(word.dataset.index) === index) {
                    console.log('test');
                    word.children.forEach(sideword => {
                      if(sideword.dataset.side == side) {
                      sideword.classList.add('active');
                    } else { 
                      sideword.classList.remove('active');
                    }
                    });
                    
                  }
                });
              }
            });
          })
        })
      }
    });
    
  //  that.nextElementSibling.querySelector('.flickity-page-dot[data-index="0"]').setAttribute('aria-current', true);
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      if (fromElement.dataset.type === 'video') {
        const videoElement = theme.utils.visibleMedia(fromElement.querySelectorAll('video-media'));
        videoElement?.pause();
      }

      if (toElement.dataset.type === 'image-comparison') {
        const animateElement = toElement.querySelector('animate-element');
        animateElement?.refresh();
        const imageComparison = toElement.querySelector('image-comparison');
        if (imageComparison) {
          imageComparison.reinit();
        }
      }

      if (toElement.dataset.type === 'image') {
        const animateElement = toElement.querySelector('animate-element');
        animateElement?.refresh();
      }
      else {
        const videoElement = theme.utils.visibleMedia(toElement.querySelectorAll('video-media'));
        videoElement?.play();
      }
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }));
  }

  select(selectedIndex) {
    this.slider.select(selectedIndex);
  }

 /* onReady(selectedElement) {
    if (selectedElement.dataset.type === 'video') {
      const videoElement = theme.utils.visibleMedia(selectedElement.querySelectorAll('video-media'));
      videoElement?.play();
    }
    if (!theme.config.isTouch) {
      const animateElement = selectedElement.querySelector('animate-element');
      animateElement?.refresh();
    }
  }*/

  onReady(selectedElement) {
    if (!this.classList.contains('loaded')) {
      this.classList.add('loaded'); // Mark the element as initialized
      return; // Skip animation on first load
    }
  
    if (selectedElement.dataset.type === 'video') {
      const videoElement = theme.utils.visibleMedia(selectedElement.querySelectorAll('video-media'));
      videoElement?.play();
    }
  
    if (!theme.config.isTouch) {
      const animateElement = selectedElement.querySelector('animate-element');
      animateElement?.refresh();
    }
  }
}
customElements.define('slideshow-element', SlideshowElement);

class SlideshowWords extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', (event) => this.selectedIndex = event.detail.currentPage);
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      this.transition(this.items[parseInt(oldValue)], this.items[parseInt(newValue)]);
    }
  }

  transition(fromElement, toElement) {
      this.items.forEach((item) => {
        item.setAttribute('aria-current', parseInt(item.dataset.index) === parseInt(this.selectedIndex) ? 'true' : 'false');
        Motion.animate(item.children, { y: [50, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5 });
      });  
  }
}
customElements.define('slideshow-words', SlideshowWords);

class ControlButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  static get observedAttributes() {
    return ['paused'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'paused') {
      if (oldValue === null && newValue === '') {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:pause', { bubbles: true }));
      }

      if (newValue === null) {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:play', { bubbles: true }));
      }
    }
  }

  onClick() {
    this.paused ? this.removeAttribute('paused') : this.setAttribute('paused', '');
  }
}
customElements.define('control-button', ControlButton, { extends: 'button' });

class QuickView extends XModal {
  constructor() {
    super();
  }

  get selector() {
    return '.quick-view__content';
  }

  beforeShow() {
    super.beforeShow();
    this.quickview();
  }

  afterShow() {
    super.afterShow();

    document.dispatchEvent(new CustomEvent('quickview:open', {
      detail: {
        productUrl: this.dataset.productUrl
      }
    }));
  }

  afterHide() {
    super.afterHide();

    const drawerContent = this.querySelector(this.selector);
    drawerContent.innerHTML = '';
  }

  quickview() {
    const drawerContent = this.querySelector(this.selector);
    const productUrl = this.dataset.productUrl.split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector(this.selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);
          theme.a11y.trapFocus(this, this.focusElement);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.dataset.productUrl
            }
          }));
        }, 200);
      })
      .catch(e => {
        console.error(e);
      });
  }

  setInnerHTML(element, innerHTML) {
    element.innerHTML = innerHTML;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);

class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.scopeFrom = document.getElementById(this.getAttribute('form'));
    this.scopeTo = document.querySelector('.footer_bottom');

    if (!this.scopeFrom || !this.scopeTo) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    intersectionObserver.observe(this.scopeFrom);
    intersectionObserver.observe(this.scopeTo);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.scopeFrom) {
        this.scopeFromPassed = entry.boundingClientRect.bottom < 0;
      }
      if (entry.target === this.scopeTo) {
        this.scopeToReached = entry.isIntersecting;
      }
    });

    if (this.scopeFromPassed && !this.scopeToReached) {
      Motion.animate(this, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
    else {
      Motion.animate(this, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
  }
}
customElements.define('sticky-buy-button', StickyBuyButton);

class StickyVariantMedia extends HTMLElement {
  constructor() {
    super();
    
    this.onVariantChangedListener = this.onVariantChanged.bind(this);
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get image() {
    return this._image = this._image ||  this.querySelector('img');
  }

  get widths() {
    return this.getAttribute('widths').split(',').map((width) => parseInt(width));
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChangedListener);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('variant:change', this.onVariantChangedListener);
  }

  onVariantChanged(event) {
    const currentVariant = event.detail.variant;
    if (!currentVariant.featured_media) return;

    const image = new Image(currentVariant.featured_media.preview_image.width, currentVariant.featured_media.preview_image.height);

    image.alt = currentVariant.featured_media.alt;
    image.src = currentVariant.featured_media.preview_image.src;
    image.srcset = this.generateSrcset(currentVariant.featured_media.preview_image);
    image.sizes = this.image.sizes;

    this.replaceChildren(image);
  }

  generateSrcset(image) {
    return this.widths.filter((width) => width <= image.width).map((width) => {
      return `${image.src}&width=${width} ${width}w`;
    }).join(', ');
  }
}
customElements.define('sticky-variant-media', StickyVariantMedia);

class StickyVariantTitle extends HTMLElement {
  constructor() {
    super();
    
    this.onVariantChangedListener = this.onVariantChanged.bind(this);
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChangedListener);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('variant:change', this.onVariantChangedListener);
  }

  onVariantChanged(event) {
    const currentOptions = this.dataset.options;
    const currentVariant = event.detail.variant.title;

    const parts1 = currentOptions.split('/').map(s => s.trim());
    const parts2 = currentVariant.split('/').map(s => s.trim());

    let html = '';
    for (let i = 0; i < parts1.length; i++) {
  //    if (parts1[i].toLowerCase() === 'color') continue;
      const className = `sticky-option-${parts1[i].toLowerCase()}`;
      html += `<div class="${className} flex gap-1"><span>${parts1[i]}:</span><span class="color-light">${parts2[i]}</span></div>`;
    }
    
    this.innerHTML = html;

    document.querySelector('sticky-buy-button').classList.add('variant-selected');
    
  }
}
customElements.define('sticky-variant-title', StickyVariantTitle);

class FiltersToggle extends HoverButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);

    if (theme.config.hasLocalStorage) {
      const expanded = window.localStorage.getItem(`${theme.settings.themeName}:filters-toogle`) || 'false';
      this.onToggle(expanded === 'true');
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.onToggle(this.expanded);
  }

  onToggle(expanded) {
    this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('xl:block', !expanded);

    if (theme.config.hasLocalStorage) {
      window.localStorage.setItem(`${theme.settings.themeName}:filters-toogle`, expanded);
    }
  }
}
customElements.define('filters-toogle', FiltersToggle, { extends: 'button' });

/* CUSTOM JS */

/* Enhanced collections slider */
/*(function () {
  const enhancedCollections = document.querySelector('.enhanced-collections-slider');
  if (!enhancedCollections) return;

  const activeItem = enhancedCollections.querySelector('.active-collection-item');
  const defaultIndex = activeItem
    ? Array.from(enhancedCollections.children).indexOf(activeItem)
    : 0;

  function initEnhancedCollectionsSlider() {
    if (!enhancedCollections || enhancedCollections.classList.contains('flickity-enabled')) return;

    const flkty = new Flickity(enhancedCollections, {
      cellSelector: '.enhanced-collections-item',
      initialIndex: defaultIndex,
      pageDots: false,
      prevNextButtons: true,
      imagesLoaded: true,
      freeScroll: true,
      wrapAround: false,
      cellAlign: 'left',
      contain: true,
      arrowShape: {
        x0: 15,
        x1: 60, y1: 40,
        x2: 65, y2: 35,
        x3: 25
      },
      on: {
        ready: function () {
          setTimeout(() => {
            this.element.setAttribute('loaded', '');
          });
        }
      }
    });

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        const blocks = Array.from(enhancedCollections.querySelectorAll('.enhanced-collections-item'));
        const index = blocks.indexOf(event.target);
        if (index >= 0) flkty.select(index, false, true);
      });
    }
  }
  initEnhancedCollectionsSlider();
})();*/

document.addEventListener("DOMContentLoaded", () => {
  const sections = document.querySelectorAll(".enhanced-collections-section");

  sections.forEach((section) => {
    const slider = section.querySelector(".enhanced-collections-slider");
    const btnPrev = section.querySelector(".enhanced-collections-arrow-left");
    const btnNext = section.querySelector(".enhanced-collections-arrow-right");

    if (!slider || !btnPrev || !btnNext) return;

    const updateArrows = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;

      if (maxScroll <= 0) {
        btnPrev.style.display = "none";
        btnNext.style.display = "none";
        return;
      }

      btnPrev.style.display = "flex";
      btnNext.style.display = "flex";

      btnPrev.style.opacity = slider.scrollLeft <= 0 ? "0.3" : "1";
      btnNext.style.opacity = slider.scrollLeft >= maxScroll - 2 ? "0.3" : "1";
    };

    const scrollAmount = () => {
      const item = slider.querySelector(".enhanced-collections-item");
      return item ? item.offsetWidth * 2 : 200;
    };

    btnPrev.addEventListener("click", () => {
      slider.scrollLeft -= scrollAmount();
    });

    btnNext.addEventListener("click", () => {
      slider.scrollLeft += scrollAmount();
    });

    slider.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);

    updateArrows();

    const active = slider.querySelector(".active-collection-item");
    if (active) {
      const left = active.offsetLeft - slider.offsetLeft * 2;
      slider.scrollLeft = left;
    }
  });
});



/* Open cart drawer based on URL */
document.addEventListener('DOMContentLoaded', function() {
  if (window.location.href.indexOf('cart-drawer-open') > 0) {
    document.querySelector('.cart-drawer-button').click();
  }
});

/* Collection - Content blocks slider */
document.addEventListener('DOMContentLoaded', function() {
  var homepageCallouts = document.querySelectorAll('.callouts-section .callouts');
  homepageCallouts.forEach((callouts) => {
      if (callouts != null) {
        homepageCalloutsSlider = new Flickity(callouts, {
          pageDots: true,
          prevNextButtons: false,
          watchCSS: true,
          wrapAround: true,
          autoPlay: 3000,
          cellAlign: 'left',
          on: {
            ready: function() {
              setTimeout(() => this.element.setAttribute('loaded', ''));
            }
          }
        });
      }
  });
});

/* Collection - Content blocks slider */
document.addEventListener('DOMContentLoaded', function() {
  var contentBlocksContainers = document.querySelectorAll('.content-blocks-container');
  contentBlocksContainers.forEach((contentBlocks) => {
    let options = {
      pageDots: false,
      prevNextButtons: false,
      cellAlign: window.innerWidth > 767 ? 'center' : 'left',
      wrapAround: window.innerWidth > 767,
      contain: window.innerWidth <= 767,
      on: {
        ready: function () {
          setTimeout(() => this.element.setAttribute('loaded', ''));
        }
      }
    };
    new Flickity(contentBlocks, options);
  });
});

/* Rivo reload */
function rivoReload() {
  let rivoAccountWidget = document.getElementById("rivo-account-slideout");
  if (rivoAccountWidget) {
    RivoProfileAlpine.$data(rivoAccountWidget).initializeFavoriteButtons();
  }
}

/* Color swatches */
function collectionColorSwatches(el) {
  document.querySelectorAll(el).forEach(swatches => {

  // Variables
  const swatchList = swatches;
  const colorSwatches = swatches.querySelectorAll('.color-swatch-custom')
  const activeSwatch = swatches.querySelector('.color-swatch-custom.active-swatch');
  const activeIndex = Array.from(colorSwatches).indexOf(activeSwatch);
  const leftArrow = swatches.previousElementSibling;
  const rightArrow = swatches.nextElementSibling;
  const visibleSwatches = 6;
   
  const scrollStep = 150;

  // Easing function (ease-out)
  function easeOutQuad(t) {
    return t * (2 - t);
  }

  // Smooth scroll implementation
  function smoothScroll(targetScrollLeft) {
    const startScrollLeft = swatches.scrollLeft;
    const distance = targetScrollLeft - startScrollLeft;
    const duration = 500; // Duration in ms
    let startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1); // Ensure progress does not exceed 1
      const easedProgress = easeOutQuad(progress);
      swatches.scrollLeft = startScrollLeft + distance * easedProgress;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }
    requestAnimationFrame(step);
  }

  // Update arrow visibility for this swatch group
  function updateArrows() {
    const maxScrollLeft = swatches.scrollWidth - swatches.clientWidth;

    leftArrow.disabled = swatches.scrollLeft <= 0;
    rightArrow.disabled = swatches.scrollLeft >= maxScrollLeft;
  }

  // Arrow click handlers for this swatch group
  leftArrow.addEventListener('click', () => {
    const targetScrollLeft = swatches.scrollLeft - scrollStep;
    smoothScroll(targetScrollLeft);
  });

  rightArrow.addEventListener('click', () => {
    const targetScrollLeft = swatches.scrollLeft + scrollStep;
    smoothScroll(targetScrollLeft);
  });

  // Scroll event listener for this swatch group
  swatches.addEventListener('scroll', updateArrows);

  // Initial update on page load
  updateArrows();

  // Swatches hover animation  
  colorSwatches.forEach(swatch => {
    swatch.addEventListener('mouseenter', () => {
      if (swatch.closest('.color-swatches').classList.contains('ajax-updated')) {
        if (!swatch.classList.contains('ajax-active')) {
          swatch.style.width = '26px';
          swatch.style.borderRadius = '14px';
          swatch.closest('.color-swatches').querySelector('.ajax-active').style.width = '14px';
          swatch.closest('.color-swatches').querySelector('.ajax-active').style.borderRadius = '50%';
        }
      } else {
        if (swatch !== activeSwatch) {
          swatch.style.width = '26px';
          swatch.style.borderRadius = '14px';
          activeSwatch.style.width = '14px';
          activeSwatch.style.borderRadius = '50%';
        }  
      }
    });

    swatch.addEventListener('mouseleave', () => {
      if (swatch.closest('.color-swatches').classList.contains('ajax-updated')) {
        if (!swatch.classList.contains('ajax-active')) {
          swatch.style.width = '14px';
          swatch.style.borderRadius = '50%';
          swatch.closest('.color-swatches').querySelector('.ajax-active').style.width = '26px';
          swatch.closest('.color-swatches').querySelector('.ajax-active').style.borderRadius = '14px';
        }
      } else {
        if (swatch !== activeSwatch) {
          swatch.style.width = '14px';
          swatch.style.borderRadius = '50%';
          activeSwatch.style.width = '26px';
          activeSwatch.style.borderRadius = '14px';
        }
      }
    });

    swatch.addEventListener('click', () => {
      colorSwatches.forEach(swatch => {
        swatch.classList.remove('ajax-active');
      });
      swatch.classList.add('ajax-active');
      swatch.closest('.color-swatches').classList.add('ajax-updated');
      var url = swatch.getAttribute('data-url').split('?')[0] + '?view=grid-ajax';
      var product = swatch.closest('.product-card');
      var swatchesSelector = `.product-card[data-product-id="${product.dataset.productId}"] .color-swatches`;
      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 400) {
          var data = xhr.responseText;
          var tempDiv = document.createElement('div');
          tempDiv.innerHTML = data;
  
          var newMedia = tempDiv.querySelector('.product-card__media');
          var newTitle = tempDiv.querySelector('.product-card__content .product-card__title');
          var newSwatchTitle = tempDiv.querySelector('.product-card__content .color-swatches-title');
          var newRivoBtn = tempDiv.querySelector('.product-card__content rivo-favorite-button');
          var newMobileQuickAddTrigger = tempDiv.querySelector('.product-card__content .mobile-quick-view-trigger');
          var newPrice =  tempDiv.querySelector('.product-card__content .price');
  
          var productMedia = product.querySelector('.product-card__media');
          var productTitle = product.querySelector('.product-card__content .product-card__title');
          var swatchTitle = product.querySelector('.product-card__content .color-swatches-title');
          var rivoBtn = product.querySelector('.product-card__content rivo-favorite-button');
          var mobileQuickAddTrigger = product.querySelector('.product-card__content .mobile-quick-view-trigger');
          var price =  product.querySelector('.product-card__content .price');

          if (newMedia && productMedia) {
            productMedia.innerHTML = newMedia.innerHTML;
          }
          if (newPrice && price) {
            price.innerHTML = newPrice.innerHTML;
          }
          if (newTitle && productTitle) {
            productTitle.innerHTML = newTitle.innerHTML;
          }
          if (newRivoBtn && rivoBtn) {
            rivoBtn.setAttribute('data-product-id', newRivoBtn.getAttribute('data-product-id'));
          }
          if (newMobileQuickAddTrigger && mobileQuickAddTrigger) {
          //  mobileQuickAddTrigger.setAttribute('aria-controls', newMobileQuickAddTrigger.getAttribute('aria-controls'));
            mobileQuickAddTrigger.setAttribute('aria-controls-trigger', newMobileQuickAddTrigger.getAttribute('aria-controls-trigger'));
          }
          if (newSwatchTitle && swatchTitle) {
            swatchTitle.innerHTML = newSwatchTitle.innerHTML;
          }
          productCardAtc(); 
          rivoReload();
        }
      };
      xhr.send();
      
    });
  });
});
}
// Initialize color swatches functionality
collectionColorSwatches('.color-swatches');

/* Mobile quick view trigger */
document.addEventListener('click', function (e) {
  const trigger = e.target.closest('.mobile-quick-view-trigger');
  if (!trigger) return;

  const controlsValue = trigger.getAttribute('aria-controls-trigger');
  if (!controlsValue) return;

  const productCard = trigger.closest('.product-card');
  if (!productCard) return;

  const matchingButton = productCard.querySelector(`.quick-view__button[aria-controls="${controlsValue}"]`);
  if (matchingButton) {
    matchingButton.click();
  }
});

/* Filters - styles/colorways toggle */
/*function stylesColorwaysToggle() {
  var toggles = document.querySelectorAll('.styles-colorways-toggle');
  toggles.forEach(function(toggle) {
    var stylesColorwaysFilter = document.querySelector('.filter-type-main-style-color input');
    if (stylesColorwaysFilter !== null) {
      var toggleButtons = toggle.querySelectorAll('[data-toggle]');
      toggleButtons.forEach(function(button) {
        button.addEventListener('click', function() {
          var isStylesButton = button.getAttribute('data-toggle') === 'styles';
          document.querySelectorAll('.styles-colorways-toggle').forEach(function(group) {
            group.classList.toggle('toggle-active-styles', isStylesButton);
          });
          if (stylesColorwaysFilter.checked !== isStylesButton) {
            console.log('test');
            stylesColorwaysFilter.click();
          }
        });
      });
    }
  });
}*/
/*function stylesColorwaysToggle() {
  var toggles = document.querySelectorAll('.styles-colorways-toggle');
  var stylesColorwaysFilter = document.querySelector('.filter-type-main-style-color input');

  if (!stylesColorwaysFilter) return;

  toggles.forEach(function(toggle) {
    var toggleButtons = toggle.querySelectorAll('[data-toggle]');

    toggleButtons.forEach(function(button) {
      var clonedButton = button.cloneNode(true); // Clone button to remove old events
      button.parentNode.replaceChild(clonedButton, button);

      clonedButton.addEventListener('click', function() {
        var isStylesButton = clonedButton.getAttribute('data-toggle') === 'styles';

        document.querySelectorAll('.styles-colorways-toggle').forEach(function(group) {
          group.classList.toggle('toggle-active-styles', isStylesButton);
        });

        if (stylesColorwaysFilter.checked !== isStylesButton) {
          stylesColorwaysFilter.click();
        }
      });
    });
  });
}*/
function stylesColorwaysToggle() {
  const stylesColorwaysFilter = document.querySelector('.filter-type-main-style-color input');
  if (!stylesColorwaysFilter) return;

  // Replace and reference updated container
  let container = document.querySelector('.styles-colorways-toggle-container');
  if (container) {
    const clonedContainer = container.cloneNode(true);
    container.parentNode.replaceChild(clonedContainer, container);
    container = clonedContainer; // update reference

    container.addEventListener('click', () => {
      const isNowStyles = !container.classList.contains('toggle-active-styles');
      applyToggle(isNowStyles);
    });
  }

  // Handle .styles-colorways-toggle [data-toggle] buttons
  const toggleGroups = document.querySelectorAll('.styles-colorways-toggle');
  toggleGroups.forEach(group => {
    const buttons = group.querySelectorAll('[data-toggle]');

    buttons.forEach(button => {
      const clonedButton = button.cloneNode(true);
      button.parentNode.replaceChild(clonedButton, button);

      clonedButton.addEventListener('click', () => {
        const isStyles = clonedButton.getAttribute('data-toggle') === 'styles';
        applyToggle(isStyles);
      });
    });
  });

  // Shared logic: toggles everything together
  function applyToggle(isStyles) {
    if (container) {
      container.classList.toggle('toggle-active-styles', isStyles);
    }

    toggleGroups.forEach(group => {
      group.classList.toggle('toggle-active-styles', isStyles);
    });

    if (stylesColorwaysFilter.checked !== isStyles) {
      stylesColorwaysFilter.click();
    }
  }

  // Script for sorting
  const sortItems = document.querySelectorAll('.sort-dropdown li[data-value]');

  if (!sortItems.length) return;

  sortItems.forEach(item => {
    item.addEventListener('click', () => {
      const selectedValue = item.getAttribute('data-value');

      // Trigger matching radio input in <facet-sort>
      const input = document.querySelector(`#SortBy-${selectedValue}`);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Sync .active-sort across ALL sort lists
      sortItems.forEach(i => {
        i.classList.toggle('active', i.getAttribute('data-value') === selectedValue);
      });
    });
  });

  const sortMobileDrawerItems = document.querySelectorAll('#SortDrawer .sort-listbox input');

  if(sortMobileDrawerItems.length > 0) {
    sortMobileDrawerItems.forEach(item => {
      item.addEventListener('change', () => {
        document.querySelector('button[aria-controls="SortDrawer"]').click();
      });
    })
  }
    
}

// Initialize styles/colorways toggle
stylesColorwaysToggle();

/* Collection layout switcher */
var layoutButtons = document.querySelectorAll('.collection__layout-button');
if (layoutButtons.length) {
  let layoutButtonsMobile = document.querySelectorAll('.collection__layout-button-mobile');
  layoutButtons.forEach(function(button) {
    button.addEventListener('click', function() {
  
      var newLayoutMode = this.getAttribute('data-layout-mode'); // Get the new layout mode
      // Send the fetch request to update the layout
  /*    fetch("/cart/update.js", {
        body: JSON.stringify({
          attributes: {
            'collection_layout': newLayoutMode
          }
        }),
        credentials: 'same-origin',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });*/
      // Update the product grid layout
      document.getElementById('ProductGridContainer').setAttribute('data-grid-layout', newLayoutMode);

      let event = new Event('layout-change');
      document.dispatchEvent(event);
      document.querySelectorAll('secondary-media').forEach(function(el) {
        el.dispatchEvent(event);
      });

      const scrollTarget = document.querySelector('.collection__content');
      if (scrollTarget) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      
      Motion.animate(document.getElementById('ProductGridContainer').querySelectorAll('.grid'), { y: [50, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5, delay: theme.config.motionReduced ? 0 : Motion.stagger(0.01) });
      layoutButtons.forEach(function(btn) {
        btn.classList.remove('is-selected');
      });
      this.classList.add('is-selected');

      layoutButtonsMobile.forEach(function(btn) {
        if(newLayoutMode == btn.dataset.layoutMode) {
          btn.classList.add('is-selected');
        } else {
          btn.classList.remove('is-selected');
        }
      });     
    });
  });
  layoutButtonsMobile.forEach(function(btn) {
    btn.addEventListener('click', function() {
      let target = btn.dataset.target;
      console.log(target);
      document.querySelector(`.collection__layout-button[data-layout-mode="${target}"]`).click();
    });
  });
}

/* Product card - ATC on desktop */
function productCardAtc() {
  // Attach a single event listener for the product-form submit buttons
  document.addEventListener('click', function(event) {
    const productInput = event.target.closest('.product-grid-option input');
    const gridInput = event.target.closest('.product-grid-option-values input');

    // Handle option input changes and availability
    if (productInput) {
      checkOptionsSelected(productInput);
      const wrapper = productInput.closest('.collection-variants');
      const optionName = productInput.getAttribute('name').split('-')[0];
      const optionValue = productInput.value;
      updateAvailableOptions(wrapper, optionName, optionValue);
    }

    // Handle grid option selection and update the ATC button accordingly
    if (gridInput) {
      const form = gridInput.closest('.collection-variants');
      const mainSelector = form.querySelector('.product-grid-main-selector');
      const atcButton = form.querySelector('button');
      let currentValue = '';
      const radioSelectors = form.querySelectorAll('.product-grid-option-values');

      radioSelectors.forEach((selector, index) => {
        if (selector.dataset.optionName === 'color') {
          const firstInput = selector.querySelector('input:first-child');
          if (firstInput) firstInput.checked = true;
        }

        const checkedInput = selector.querySelector('input:checked');
        if (checkedInput) {
          currentValue += checkedInput.value;
        }

        if (index !== radioSelectors.length - 1) {
          currentValue += ' / ';
        }
      });

      const selectedOption = mainSelector.querySelector(`option[data-title="${currentValue}"]`);
      updateAtcButton(selectedOption, atcButton);
    }
  });

  // Helper function to check if all product options are selected
  function checkOptionsSelected(element) {
    const wrapper = element.closest('.collection-variants');
    const atcContainer = wrapper.querySelector('.product-atc-container');
    const productInputs = wrapper.querySelectorAll('.product-grid-option');
    let unselectedOptions
    productInputs.forEach(inputContainer => {
      const checkedOption = inputContainer.querySelector('input:checked');
      const optionName = inputContainer.dataset.option;

      if (!checkedOption) {
        wrapper.querySelector('.collection-atc-submit').innerHTML == 'Select ' + optionName;
        wrapper.querySelector('.collection-atc-submit').classList.add(optionName);
      }
    });

  }

  // Helper function to update available options based on user selection
  function updateAvailableOptions(wrapper, optionName, optionValue) {
    const availableOptions = wrapper.querySelector('.product-atc-container').dataset.options.split(',');
    const inputSelector = optionName === 'Size' ? 'input[name^="Length"]' : 'input[name^="Size"]';
    const inputsToUpdate = wrapper.querySelectorAll(`.product-grid-option ${inputSelector}`);

    inputsToUpdate.forEach(input => {
      const option = optionName === 'Size' ? `${optionValue} - ${input.value}` : `${input.value} - ${optionValue}`;
      input.classList.toggle('disabled', !availableOptions.includes(option));
    });
  }

  // Helper function to update the ATC (Add to Cart) button
  function updateAtcButton(selectedOption, atcButton) {

    const wrapper = atcButton.closest('.collection-variants');
    const atcContainer = wrapper.querySelector('.product-atc-container');
    const productInputs = wrapper.querySelectorAll('.product-grid-option');
    let buttonText = '';
    let unselectedVariants = 0;
    productInputs.forEach(inputContainer => {
      const checkedOption = inputContainer.querySelector('input:checked');
      const optionName = inputContainer.dataset.option;
      if (!checkedOption) {
        unselectedVariants++;
        buttonText = 'Select ' + optionName;
      }
    });
    if(unselectedVariants > 0 && buttonText != '') {
      atcButton.textContent = buttonText;
      atcButton.disabled = true;
    } else {
      if (selectedOption) {
        atcButton.dataset.variantId = selectedOption.value;
        atcButton.nextElementSibling.value = selectedOption.value;
        selectedOption.selected = true;
        if (selectedOption.dataset.availability === 'true') {
          atcButton.textContent = 'Add to Cart';
          atcButton.disabled = false;
        } else {
          atcButton.textContent = 'Sold Out';
          atcButton.disabled = true;
        }
      } else {
        atcButton.textContent = 'Unavailable';
        atcButton.disabled = true;
      }
    }
    
  }

  // Handle desktop ATC hover state
  var collectionAtcBlock = document.querySelectorAll('.collection-atc');
  collectionAtcBlock.forEach(el => {
    el.addEventListener('mouseover', () => {
      el.closest('.product-card').classList.add('atc-form-visible');
    });
    el.addEventListener('mouseleave', () => {
      el.closest('.product-card').classList.remove('atc-form-visible');
    });                  
  });
}

// Initialize
productCardAtc();

document.addEventListener('collection:reloaded', function() {
  productCardAtc();
  stylesColorwaysToggle();
  collectionColorSwatches('.color-swatches');
});
document.addEventListener('recentlyViewed:loaded', function() {
  productCardAtc();
  collectionColorSwatches('.color-swatches');
  rivoReload();
});
document.addEventListener('recommendations:loaded', function() {
  productCardAtc();
  collectionColorSwatches('.color-swatches');
  rivoReload();
});


/* Status widget - v3 (shopify search API) */
document.addEventListener('status:loaded', (event) => {
  var statusCollections = window.StatusAccounts.collections;
  for (const [key, value] of Object.entries(statusCollections)) {
    var widgetId = key;
    var statusWidget = document.querySelector(`.status-shopping-block[type="${key}"]`);
    if (statusWidget !== null) {
      let statusProducts = value.map(item => item.productId);
      if (statusProducts.length > 0) {
      var statusText = {'recentlyViewed': 'Recently Viewed Products',
                        'popularToday': 'Popular Products',
                        'savedFromCart': 'Products Saved From Cart',
                        'wishlist': 'Wishlist Products'};
      var statusProductsHeading = document.createElement('div');
      statusProductsHeading.className = 'status-products-heading status-products-heading title-wrapper leading-none heading title-lg';   
      statusProductsHeading.innerHTML = statusText[key];
      document.querySelector(`.status-shopping-block[type="${key}"]`).parentNode.appendChild(statusProductsHeading);  
      let statusProductsContainer = document.createElement('div');
      statusProductsContainer.className = `card-grid card-grid--4 mobile:card-grid--2 grid relative status-widget-${key}`;
      document.querySelector(`.status-shopping-block[type="${key}"]`).parentNode.appendChild(statusProductsContainer);
  
      async function getProductsByIds(ids) {
        const search = encodeURI(ids.map(id => {
            return `id:${id}`;
        }).join(" OR "));
        await fetch(`/search?q=${search}&type=product&view=grid-ajax`)
        .then((response) => {
          return response.text();
        })
        .then((html) => {
          statusProductsContainer.innerHTML = html;
          var statusProductIndex = 0;
          statusProductsContainer.querySelectorAll('.product-card').forEach(product => {
            var index = statusProductIndex++;
            var statusProductInputs = product.querySelectorAll('.product-grid-option-values input');
            var statusProductLabels = product.querySelectorAll('.product-grid-option-values label');
            var totalItems = Math.min(statusProductInputs.length, statusProductLabels.length);   
            for (var i = 0; i < totalItems; i++) {
              var input = statusProductInputs[i];
              var label = statusProductLabels[i];     
              var suffix = '-' + widgetId + '-' + index;
              input.setAttribute('name', (input.getAttribute('name') || '') + suffix);
              input.setAttribute('id', (input.getAttribute('id') || '') + suffix);
              label.setAttribute('for', (label.getAttribute('for') || '') + suffix);
            }
          });
        })
        .then((html) => {
            collectionColorSwatches('.status-shopping-block-section .color-swatches');
            productCardAtc();
        });
      }
      getProductsByIds(statusProducts);
     }
   }
 }
})


/* Status button */

let init = () => {
  var statusButton = document.querySelectorAll('.status-favorite-button');
    console.log(window.StatusAccounts.wishlist);
    statusButton.forEach(button => {
      let productId = button.dataset.productId;
      let productTitle = button.dataset.productTitle;
      const productIds = window.StatusAccounts.wishlist;
      const productIdSet = new Set(productIds);
      let isActive = window.StatusAccounts.wishlist.includes(productId);
      if (productIdSet.has(productId)) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  statusButton.forEach(button => {
    button.addEventListener('click', () => {
      let favorites = window.StatusAccounts.wishlist;
      let productId = button.dataset.productId;
      let isActive = button.classList.contains('active');
      if (isActive) {
        button.classList.remove('active');
        let browserEvent = new CustomEvent('status:item:remove',
          {
            detail: {
              productId,
              triggerToast: true,
            }
          })
        document.dispatchEvent(browserEvent)
      } else {
        button.classList.add('active');
        let browserEvent = new CustomEvent('status:item:save',
          {
            detail: {
              productId,
              triggerToast: true,
            }
          })
        document.dispatchEvent(browserEvent)
      }
    })
  });
}

if (window.StatusAccounts?.customer) {
  init()
} else {
  document.addEventListener('status:loaded', (event) => {
    init()
  });
}

/* Additional details box */
let additionalDetailsTrigger = document.querySelectorAll('.additional-details-trigger');
if (additionalDetailsTrigger.length > 0) {
  additionalDetailsTrigger.forEach(trigger => {
    trigger.addEventListener('click', () => {
      trigger.closest('.additional-details-container').classList.toggle('active');
    })
  })
}

let filterTrigger = document.querySelectorAll('.collection__topbar .filter-trigger');
if (filterTrigger.length > 0) {
  filterTrigger.forEach(trigger => {
    trigger.addEventListener('click', () => {
      document.querySelectorAll('.facet-form .details').forEach(filter => {
        filter.open = false;
      });
      let filter = trigger.dataset.filter;      
      setTimeout(function() {
        let filterDropdown = document.querySelector(`.facet-form .filter-type-${filter}`);
        if (filterDropdown !== null) {   
          document.querySelector('.facet-drawer .drawer__scrollable').scrollTo({
            top: filterDropdown.offsetTop,
            behavior: "instant"
          });
          filterDropdown.open = true;
        }
      }, 400);
    });
  });
}

/* PDP Sticky ATC */
function stickyAtcAnchor() {
  let stickyATC = document.querySelector('.sticky-atc');
  if (stickyATC != null) {
    stickyATC.addEventListener('click', () => {
      const scrollTarget = document.querySelector('.product-content-form');
      if (scrollTarget) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    })
  }
}
document.addEventListener("DOMContentLoaded", () => {
  stickyAtcAnchor();
});

/* PDP desktop description trigger */
function productDescription() {
  let descriptionTrigger = document.querySelector('.description-trigger-desktop');
  if (descriptionTrigger != null) {
    descriptionTrigger.addEventListener('click', () => {
      descriptionTrigger.closest('.product__info').classList.toggle('description-modal-active');
      document.querySelector('body').classList.toggle('has-modal-open');
    }) 
    document.querySelector('.desktop-description-overlay').addEventListener('click', function() {
      descriptionTrigger.closest('.product__info').classList.remove('description-modal-active');
      document.querySelector('body').classList.remove('has-modal-open');
    });
  }
  // PDP desktop description blocks
/*  let descriptionBlocks = document.querySelectorAll('.description-block-trigger');
  if (descriptionBlocks.length > 0) {
    descriptionBlocks.forEach(el => {
      el.addEventListener('click', () => {
        el.nextElementSibling.classList.add('active');
        el.closest('.product-content-description-info ').classList.add('active');
      });
    });
    let descriptionBlocksClose = document.querySelectorAll('.description-block-close');
    descriptionBlocksClose.forEach(el => {
      el.addEventListener('click', () => {
        el.closest('.description-block-content').classList.remove('active');
        el.closest('.product-content-description-info ').classList.remove('active');
      });
    });
  }*/

  const descriptionBlocks = document.querySelectorAll('.description-block-trigger details');
  if (descriptionBlocks.length > 0) {
    descriptionBlocks.forEach(detail => {
      detail.addEventListener('click', () => {
        if (detail.open) {
          descriptionBlocks.forEach(other => {
            if (other !== detail) other.open = false;
          });
        }
        setTimeout(() => {
          const container = detail.closest('.product-content-description-info');
          const anyOpen = Array.from(container.querySelectorAll('.description-block-trigger details')).some(d => d.open);
  
          if (anyOpen) {
            container.classList.add('accordion-open');
          } else {
            container.classList.remove('accordion-open');
          }
        }, 0);
      });
    });
  }
  
}
document.addEventListener("DOMContentLoaded", () => {
  productDescription();
});



/* Pillars section */
document.addEventListener("DOMContentLoaded", () => {
  // Select all buttons inside pillars-inner-nav
  const navButtons = document.querySelectorAll(".pillars-inner-nav .pillars-nav-button");

  // Add click event listeners to each button
  navButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const parentContainer = button.closest(".slideshow-container");

      // Get data attributes from the clicked button
      const heading = button.dataset.heading || "";
      const subheading = button.dataset.subheading || "";
      const buttonText = button.dataset.btn || "";
      const buttonUrl = button.dataset.url || "";
      const videoUrl = button.dataset.video || "";
      const index = button.closest('.banner').getAttribute("data-index"); 
      const windowWidth = window.innerWidth;
      let imageUrl = button.dataset.image || "";
      let imageLeftUrl = button.dataset.imageLeft || "";
      let imageRightUrl = button.dataset.imageRight || "";
      if (window.innerWidth < 500) {
        imageUrl = imageUrl.replace('_1000x', '_500x');
        imageLeftUrl = imageLeftUrl.replace('_1000x', '_500x');
        imageRightUrl = imageRightUrl.replace('_1000x', '_500x');
      }

      const wordElement = parentContainer.querySelector(`.slideshow-word[data-index="${index}"]`);
      const drawerContainer = parentContainer.querySelector(`.pillars-modal[data-index="${index}"]`);
      const drawerTrigger = parentContainer.querySelector(`.pillars-modal-button[data-index="${index}"]`);

      if(button.classList.contains('pillars-size-trigger')) {

        const size = button.dataset.size;
        const imageWithSizesContainer = button.closest('.image-with-sizes');
        if (button.classList.contains('active')) {
          button.classList.remove('active');
          imageWithSizesContainer.classList.remove('size-active');
          imageWithSizesContainer.dataset.size = '';
        } else {
          parentContainer.querySelectorAll('.banner .pillars-size-trigger').forEach(btn => {
            btn.classList.remove('active');
          });
          imageWithSizesContainer.classList.add('size-active');
          imageWithSizesContainer.dataset.size = button.dataset.size;
          button.classList.add('active');
        }

        if (wordElement) {
          // Update heading
          if (heading || subheading) {
            const textAnimateEl = wordElement.closest('slideshow-words');
            Motion.animate(textAnimateEl, { y: [50, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5 });
          }
          const headingElement = wordElement.querySelector(".heading");
          if (headingElement && heading) headingElement.innerHTML = heading;
  
          // Update subheading
          const subheadingElement = wordElement.querySelector(".subheading");
          if (subheadingElement && subheading) subheadingElement.innerHTML = subheading;
  
          // Update button text
          const buttonElement = wordElement.querySelector(".button .btn-text");
          if (buttonElement && buttonText) buttonElement.innerHTML = buttonText;
          // Update button URL
          const buttonLink = wordElement.querySelector(".button");
          if (buttonLink && buttonUrl) {
            buttonLink.setAttribute("href", buttonUrl);
            if (buttonText == '') {
              buttonLink.classList.add('hide-button');
            } else {
              buttonLink.classList.remove('hide-button');
            }
          }
        }

      } else {

        parentContainer.querySelectorAll('.banner .pillars-drawer-trigger').forEach(btn => {
          btn.classList.remove('active');
        });
        if (button.classList.contains('pillars-gender-toggle')) {
          parentContainer.querySelectorAll('.banner .pillars-nav-button').forEach(btn => {
            btn.classList.remove('active');
          });
          if (button.classList.contains('pillars-gender-women')) {
            button.closest('.pillars-inner-nav').classList.add('gender-active-women');
          } else {
            button.closest('.pillars-inner-nav').classList.remove('gender-active-women');            
          }
          if (button.classList.contains('pillars-gender-toggle-size-block')) {
            button.closest('.image-with-sizes').classList.remove('size-active');
            button.closest('.image-with-sizes').dataset.size = '';
          }
        }
        button.classList.add('active');

        if (wordElement) {
          // Update heading
          if (heading || subheading) {
            const textAnimateEl = wordElement.closest('slideshow-words');
            Motion.animate(textAnimateEl, { y: [50, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5 });
          }
          const headingElement = wordElement.querySelector(".heading");
          if (headingElement && heading) headingElement.innerHTML = heading;
  
          // Update subheading
          const subheadingElement = wordElement.querySelector(".subheading");
          if (subheadingElement && subheading) subheadingElement.innerHTML = subheading;
  
          // Update button text
          const buttonElement = wordElement.querySelector(".button .btn-text");
          if (buttonElement && buttonText) buttonElement.innerHTML = buttonText;
          // Update button URL
          const buttonLink = wordElement.querySelector(".button");
          if (buttonLink && buttonUrl) {
            buttonLink.setAttribute("href", buttonUrl);
            if (buttonText == '') {
              buttonLink.classList.add('hide-button');
            } else {
              buttonLink.classList.remove('hide-button');
            }
          }
        }
  
        const videoMediaElement = parentContainer.querySelector(".pillars-video video-media");
        const videoMediaElementIframe = videoMediaElement.querySelector('iframe');
        if (videoUrl != "") {  
          if (videoMediaElementIframe) {
            videoMediaElementIframe.src = `https://player.vimeo.com/video/${videoUrl}?autopause=1&background=1&autoplay=1&muted=1&loop=1&transparent=0&responsive=1&portrait=0&title=0&byline=0`;
            videoMediaElement.playerTarget().then((player) => {
              parentContainer.classList.add('pillars-video-active');
            });
          }
        } else if (imageLeftUrl != "" && imageRightUrl != "") {
          parentContainer.classList.remove('pillars-video-active');
          const mediaElementLeft = parentContainer.querySelector(".comparison__before img");
          const mediaElementRight = parentContainer.querySelector(".comparison__after img");
          if (mediaElementLeft && mediaElementRight) {
            const imageAnimateEl = mediaElementLeft.closest('animate-element');
            const newImageLeft = new Image();
            newImageLeft.src = imageLeftUrl;
            const newImageRight = new Image();
            newImageRight.src = imageRightUrl;
            newImageRight.onload = () => {
              mediaElementLeft.setAttribute("src", imageLeftUrl);
              mediaElementLeft.setAttribute("srcset", imageLeftUrl);
              mediaElementRight.setAttribute("src", imageRightUrl);
              mediaElementRight.setAttribute("srcset", imageRightUrl);
              Motion.animate(imageAnimateEl, { transform: 'scale(1.3)' }, { duration: 0 });
              setTimeout(function() {
                Motion.animate(imageAnimateEl, { transform: 'scale(1)' }, { duration: 1.3, delay: 0, easing: [0.16, 1, 0.3, 1] }).finished;         
              }, 10);
            };
          }
        } else {
          parentContainer.classList.remove('pillars-video-active');
          videoMediaElementIframe.src = "";
          // Update image
          if(imageUrl) {
            const mediaElement = parentContainer.querySelector(".pillars-image .banner__image");
            if (mediaElement) {
              const imageAnimateEl = mediaElement.closest('animate-element');
              const newImage = new Image();
              newImage.src = imageUrl;
              newImage.onload = () => {
                mediaElement.setAttribute("src", imageUrl);
                mediaElement.setAttribute("srcset", imageUrl);
                Motion.animate(imageAnimateEl, { transform: 'scale(1.3)' }, { duration: 0 });
                setTimeout(function() {
                  Motion.animate(imageAnimateEl, { transform: 'scale(1)' }, { duration: 1.3, delay: 0, easing: [0.16, 1, 0.3, 1] }).finished;         
                }, 10);
              };
            }
          }
        }  
        
      }
    });
  });
});

/* Slideshow hotspots */
document.addEventListener('DOMContentLoaded', () => {
  const hotspotElements = document.querySelectorAll('.hotspot-product');
  hotspotElements.forEach(element => {
    element.addEventListener('click', () => {
      const productsData = JSON.parse(element.dataset.products);     
      const drawerTrigger = document.querySelector('.hotspot-modal-button');
      const outputContainer = document.querySelector('.hotspot-drawer-content');
      outputContainer.innerHTML = '';

      productsData.forEach(product => {
        const productBlock = document.createElement('a');
        productBlock.href = product.url;
        productBlock.classList.add('product-block');
        const img = document.createElement('img');
        img.src = product.image;

        const contentDiv = document.createElement('div');

        const title = document.createElement('div');
        title.textContent = product.title;

        const price = document.createElement('div');
        price.innerHTML = product.price;

        const color = document.createElement('div');
        color.classList.add('text-title');
        color.textContent = product.color;

        contentDiv.appendChild(title);
        contentDiv.appendChild(price);
        contentDiv.appendChild(color);

        productBlock.appendChild(img);
        productBlock.appendChild(contentDiv);

        outputContainer.appendChild(productBlock);
      });
      drawerTrigger.click();
    });
  });
});

/* Size chart */
document.addEventListener("DOMContentLoaded", function () {
  const sizeChartModal = document.querySelector(".sizechart-tables");
  const tableCells = document.querySelectorAll(".sizechart-tables td");
  const unitElements = document.querySelectorAll(".sizechart-units a");
  const sizeChartRows = document.querySelectorAll(".sizechart-tables tr");
  let unit = "unit-us";

  // Toggle hover classes on mouseover/mouseout
 /* tableCells.forEach(cell => {
    cell.addEventListener("mouseover", () => handleHover(cell, true));
    cell.addEventListener("mouseout", () => handleHover(cell, false));
  });

  function handleHover(cell, isHover) {
    cell.classList.toggle("hover-target", isHover);

    const index = Array.from(cell.parentNode.children).indexOf(cell) + 1;
    const activeCells = [
      ...Array.from(cell.parentNode.children).slice(0, index),
      ...Array.from(cell.parentNode.previousElementSibling?.children || []).filter(
        siblingCell => siblingCell.cellIndex === cell.cellIndex
      )
    ];

    activeCells.forEach(activeCell => activeCell.classList.toggle("hover", isHover));

    document.querySelectorAll(".table-hidden-cell.hover").forEach(hiddenCell => {
      hiddenCell.closest("tr").previousElementSibling
        ?.querySelector('td[rowspan="2"]')
        ?.classList.toggle("hover", isHover);
      hiddenCell.classList.remove("hover");
    });
  }*/

  // Handle unit conversion
  unitElements.forEach(element => {
    element.addEventListener("click", e => {
      e.preventDefault();
      const isUS = element.classList.contains("unit-us");
      const isMetric = element.classList.contains("unit-metric");

      if (isUS && unit !== "unit-us") {
        setUnit("unit-us");
      } else if (isMetric && unit !== "unit-metric") {
        setUnit("unit-metric");
      }
    });
  });

  function setUnit(newUnit) {
    unitElements.forEach(element => {
      if (element.classList.contains(newUnit)) {
        element.classList.add("active");
      } else {
        element.classList.remove("active");
      }
    });

    sizeChartRows.forEach((row, rowIndex) => {
      if (rowIndex === 0) return; // Skip the first row (header)

      Array.from(row.children).slice(1).forEach(cell => {
        if (cell.tagName === "TH") return;
        const text = cell.textContent.trim();
        let convertedValue;

        if (newUnit === "unit-us") {
          if (!cell.dataset.us) {
            const value = parseFloat(text);
            if (!isNaN(value)) {
              convertedValue = (Math.floor((value / 2.54) * 2) / 2).toString();
              cell.dataset.cm = text;
              cell.textContent = convertedValue;
            }
          } else {
            cell.textContent = cell.dataset.us;
          }
        } else if (newUnit === "unit-metric") {
          if (!cell.dataset.cm) {
            if (text.includes("-")) {
              const [val1, val2] = text.split("-").map(Number);
              convertedValue =
                `${Math.floor(val1 * 2.54)}-${Math.floor(val2 * 2.54)}`;
            } else {
              const value = parseFloat(text);
              if (!isNaN(value)) {
                convertedValue = Math.floor(value * 2.54).toString();
              }
            }
            cell.dataset.us = text;
            cell.textContent = convertedValue || "";
          } else {
            cell.textContent = cell.dataset.cm;
          }
        }
      });
    });

    unit = newUnit;
  }
});

/* Localization */
function localization() {
  const form = document.getElementById('localization_form');
  const countries = form.querySelectorAll('.localization-country');
  const formInput = form.querySelector('input[name="country_code"]');
  countries.forEach(country => {
    country.addEventListener('click', function() {
      formInput.value = country.dataset.value;
      form.submit();
    })
  })
}

localization();

function localizationSearch() {
  let input = document.getElementById('localization-search').value.toUpperCase();
  const container = document.getElementById('CountryList');
  const continents = container.querySelectorAll('details');
  const countries = container.querySelectorAll('.localization-country');
  countries.forEach(country => {
    if(country.textContent.toUpperCase().indexOf(input) >= 0) {
      country.classList.remove('hidden');
    } else {
      country.classList.add('hidden');
    }
    continents.forEach(continent => {
      const countriesFound = continent.querySelectorAll('.localization-country:not(.hidden)').length;
      if (input.length == 0) {
        continent.open = false;
      } else {
        if (countriesFound > 0) {
          continent.open = true;
        } else {
          continent.open = false;
        }
      }
    })
  })
}

/* Account login message */
/*document.addEventListener("DOMContentLoaded", function () {
  const messageDiv = document.getElementById("login-message");
  if (messageDiv != null && !sessionStorage.getItem("messageDisplayed")) {
    setTimeout(() => {
      messageDiv.classList.add("visible");
      setTimeout(() => {
        messageDiv.classList.remove("visible");
      }, 5000);
      sessionStorage.setItem("messageDisplayed", "true");
    }, 1000);
  }
});*/

/* Blog/Lookbook section */
document.addEventListener("DOMContentLoaded", () => {
  const container = document.querySelector(".blog-lookbook");
  if (container) {   
    const heading = container.querySelector('h2');
    const toggleButtons = container.querySelectorAll('.tab__item');
    toggleButtons.forEach(button => {
      button.addEventListener('click', () => {
        heading.innerHTML = button.dataset.heading;
      })
    });

    Motion.inView(container, ({ target }) => {
      target.classList.add('hovered');
    }, {
      amount: 0.5 // Trigger when 50% visible
    });
    
  /*  container.addEventListener("mouseenter", () => {
      container.classList.add("hovered");
    });
    container.addEventListener("mouseleave", () => {
      container.classList.remove("hovered");
    });*/
  }
});

/* Klaviyo Signup form */
function attachKlaviyoForm(formId) {
  var formElement = document.getElementById(formId);
  if (formElement != null) {
    var selectedGender = formElement.querySelector('input.klaviyo_form_actions_gender:checked').value;
    setTimeout(function() {
      KlaviyoSubscribe.attachToForms(formElement, {
        hide_form_on_success: true,
        extra_properties: {
          $source: '$embed',
          $method_type: "Klaviyo Form",
          $method_id: 'embed',
          $gender: selectedGender,
          $consent_version: 'Embed default text',
        },
        success: function () {
          formElement.querySelector('.custom_success_message').classList.remove('hidden');
        },
      });
    }, 2000);
  }
}
attachKlaviyoForm('email_signup_footer');
attachKlaviyoForm('email_signup');

/* Breadcrumbs - save and get a previous page */
(function () {
  let currentURL = window.location.href;
  let breadcrumbContainer = document.querySelector('nav.breadcrumb');
  let storedBreadcrumbs;
  currentURL = currentURL.split("?")[0].split("#")[0];
  if (currentURL.includes('/collections/') && breadcrumbContainer != null) {
    const breadcrumbsHTML = breadcrumbContainer.innerHTML;
    sessionStorage.setItem('breadcrumbHTML', breadcrumbsHTML);
  }
    storedBreadcrumbs = sessionStorage.getItem("breadcrumbHTML");
  if (currentURL.includes('/products/') && breadcrumbContainer != null) {
    if (storedBreadcrumbs != null) {
      breadcrumbContainer.innerHTML = storedBreadcrumbs;
    } else {
      breadcrumbContainer.innerHTML = `<ul class="inline-flex gap-4 md:gap-6 text-title">
                                         <li class="flex items-center gap-4 md:gap-6">
                                           <a class="block" href="/" is="magnet-link">Home</a>
                                         </li>
                                       </ul>`
    }
  }
})();

/* Smart size chart - ATC listener */
document.addEventListener("bmi_selected_size", e => {
  var size = e.detail.size;
  var optionSize = size.substring(0, size.indexOf(' '));
  var optionLength = size.substring(size.indexOf(' ') + 1);
  const selectedSize = document.querySelector(`.variant-picker .product-form__input label[title="${optionSize}"]`);
  const selectedLength = document.querySelector(`.variant-picker .product-form__input label[title="${optionLength}"]`);
  if (selectedLength) {
    selectedLength.click();
  }
  if (selectedSize) {
    selectedSize.click();
  }
});
/*document.addEventListener("bmi_add_to_cart", e => {
  const addToCartLink = document.querySelector('.product-form__buttons .product-form__submit');
  if (addToCartLink) {
    setTimeout(() => addToCartLink.click(), 1000);
  }
})*/
document.addEventListener("bmi_ssc_loaded", e => {
  document.querySelector('.boldmetrics-container').classList.add('loaded');
  document.querySelector('.boldmetrics-container .bm-button-label').classList.add('reversed-link')
});
document.addEventListener("bmi_reload", e => {
  document.querySelector('.boldmetrics-container').classList.add('loaded');
});
function boldmetricsReload() {
  document.dispatchEvent(
    new CustomEvent('bmi_reload', {
      bubbles: true,
    })
  )
}

/* Custom Zoom */
function customZoom () {
  const galleryImages = document.querySelectorAll(".product__media[data-zoom-type='custom'] img");
  const lightbox = document.createElement("div");
  lightbox.id = "zoom-lightbox";
  lightbox.innerHTML = `<div class="zoom-lightbox-content">
                          <button class="zoom-close-btn">
                            <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                            </svg>
                          </button>
                          <div class="zoom-lightbox-thumbnails"></div>
                          <div class="zoom-lightbox-images"></div>
                        </div>`;
  document.body.appendChild(lightbox);
  const lightboxThumbnails = lightbox.querySelector(".zoom-lightbox-thumbnails");
  const lightboxImages = lightbox.querySelector(".zoom-lightbox-images");
  const closeBtn = lightbox.querySelector(".zoom-close-btn");
  galleryImages.forEach((img, index) => {
    const thumb = document.createElement("img");
    thumb.src = img.src;
    thumb.dataset.index = index;
    thumb.classList.add("zoom-lightbox-thumb");
    lightboxThumbnails.appendChild(thumb);

    const fullImgWrapper = document.createElement("div");
    fullImgWrapper.classList.add("zoom-lightbox-img-wrapper");
    
    const counter = document.createElement("span");
    counter.classList.add("zoom-image-counter");
    counter.textContent = `${index + 1} / ${galleryImages.length}`;
    
    const fullImg = document.createElement("img");
    fullImg.src = img.src;
    fullImg.classList.add("zoom-lightbox-img");
    fullImg.dataset.index = index;
    
    fullImgWrapper.appendChild(counter);
    fullImgWrapper.appendChild(fullImg);
    lightboxImages.appendChild(fullImgWrapper);

    img.addEventListener("click", function () {
      lightbox.style.display = "flex";
      setTimeout(() => lightbox.classList.add("fade-in"), 10);
      setTimeout(() => lightbox.classList.add("active"), 20);
      document.body.classList.add("has-modal-open");
      scrollToImageInstantly(index);
    });
  });

  function scrollToImageInstantly(index) {
    const targetImage = lightboxImages.querySelector(`[data-index='${index}']`);
    if (targetImage) {
      targetImage.scrollIntoView({ behavior: "instant", block: "start" });
      updateActiveThumbnail(index);
    }
  }

  function scrollToImage(index) {
    const targetImage = lightboxImages.querySelector(`[data-index='${index}']`);
    if (targetImage) {
      targetImage.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function updateActiveThumbnail(index) {
    lightboxThumbnails.querySelectorAll(".zoom-lightbox-thumb").forEach(thumb => {
      thumb.classList.remove("active");
    });
    lightboxThumbnails.querySelector(`[data-index='${index}']`).classList.add("active");
  }

  lightboxThumbnails.addEventListener("click", function (e) {
    if (e.target.classList.contains("zoom-lightbox-thumb")) {
      scrollToImage(e.target.dataset.index);
    }
  });

  function closeModal() {
    lightbox.classList.remove("fade-in");
    lightbox.classList.add("fade-out");
    setTimeout(() => {
      lightbox.classList.remove("active", "fade-out");
      document.body.classList.remove("has-modal-open");
    }, 300);
  }

  closeBtn.addEventListener("click", closeModal);

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      closeModal();
    }
  });

  lightbox.addEventListener("click", function (e) {
    if (e.target === lightbox) {
      closeModal();
    }
  });

  lightboxImages.addEventListener("scroll", function () {
    let activeIndex = null;
    let maxCoverage = 0;
    lightboxImages.querySelectorAll(".zoom-lightbox-img").forEach(img => {
      const rect = img.getBoundingClientRect();
      const imageCoverage = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      const coveragePercentage = imageCoverage / window.innerHeight;
      if (coveragePercentage > 0.5 && coveragePercentage > maxCoverage) {
        maxCoverage = coveragePercentage;
        activeIndex = img.dataset.index;
      }
    });
    if (activeIndex !== null) {
      updateActiveThumbnail(activeIndex);
    }
  });
}
document.addEventListener("DOMContentLoaded", function () {
  customZoom ();
});

/*function rebuyWidgetCarousel() {
  const container = document.querySelector('.rebuy-widget-products-primary');
  const prevBtn = document.querySelector('.rebuy-widget-primary-nav-prev');
  const nextBtn = document.querySelector('.rebuy-widget-primary-nav-next');

  if (!container || !prevBtn || !nextBtn ) return;

  const scrollAmount = 250;

  function updateArrowStates() {
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    if (container.scrollLeft <= 0) {
      prevBtn.classList.add('disabled');
    } else {
      prevBtn.classList.remove('disabled');
    }

    if (container.scrollLeft >= maxScrollLeft - 1) {
      nextBtn.classList.add('disabled');
    } else {
      nextBtn.classList.remove('disabled');
    }
  }

  prevBtn.addEventListener('click', () => {
    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  });

  nextBtn.addEventListener('click', () => {
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  });

  // Update arrows on scroll
  container.addEventListener('scroll', updateArrowStates);

  // Initial update
  updateArrowStates();
}*/
function rebuyWidgetCarousel(wrapperSelector, scrollAmount = 250) {
  const wrapper = document.querySelector(wrapperSelector);
  if (!wrapper) return;

  const container = wrapper.querySelector('.rebuy-widget-carousel');
  const prevBtn = wrapper.querySelector('.rebuy-widget-primary-nav-prev');
  const nextBtn = wrapper.querySelector('.rebuy-widget-primary-nav-next');

  if (!container || !prevBtn || !nextBtn) return;

  function updateArrowStates() {
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    prevBtn.classList.toggle('disabled', container.scrollLeft <= 0);
    nextBtn.classList.toggle('disabled', container.scrollLeft >= maxScrollLeft - 1);
  }

  prevBtn.addEventListener('click', () => {
    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  });

  nextBtn.addEventListener('click', () => {
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  });

  container.addEventListener('scroll', updateArrowStates);
  updateArrowStates();
}

/* Rebuy quick ATC */
document.addEventListener('click', async function(e) {
  const sectionID = document.querySelector('#CartDrawer').getAttribute('data-section-id');
  const btn = e.target.closest('.quick-atc');
  if (!btn) return;

  e.preventDefault();

  const variantId = btn.getAttribute('data-variant-id');
  if (!variantId) return;

  btn.classList.add('loading');

  try {
    // Add product to cart
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });

    if (!res.ok) throw new Error('Network response was not ok.');
    const addedProduct = await res.json();

    btn.classList.remove('loading');

    // Fetch updated cart sections and cart data
    const parsedState = await fetch(`/?sections[]=${encodeURIComponent(sectionID)}`)
      .then(res => res.json());

    const cartJson = await (await fetch(`${theme.routes.cart_url}`, {
      ...theme.utils.fetchConfig()
    })).json();

    cartJson.sections = parsedState;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: {
        product: parsedState
      }
    }));

  } catch (err) {
    console.error('ATC Error:', err);
    btn.textContent = 'Error';
  }
});

/* Rebuy cart widgets */
function buildRebuyWidget(id, prod) {
  let widgetId = id;
  let products = prod;
  let total_products = products.length;
  let container = document.querySelector(`.cart-drawer div[data-rebuy-id="${widgetId}"]`);
  container.innerHTML = '';
  if (products != null) {
  products.forEach(product => {
    if (product.handle != undefined) {
      let handle = product.handle;
      let url = `/products/${handle}?_source=Rebuy&_widget_id=${widgetId}&_attribution=Cart`
      let title = product.title.split(" in ")[0];
      let image = product.image.src.replace(".jpg", "_300x.jpg").replace(".png", "_300x.png");
      let price = Rebuy.util.formatMoney(product.selected_variant.price).replace('.00', '');
      let compare_at_price = Rebuy.util.formatMoney(product.selected_variant.compare_at_price);
      container.innerHTML += `<div href="${url}" class="rebuy-product flex flex-col">
                                <div class="rebuy-product-image">
                                 <img src="${image}">
                                 <button type="button" class="button button-white icon-with-text" is="hover-button" aria-controls="Quickview-${widgetId}-${product.id}" aria-expanded="false">
                                    <span class="btn-fill" data-fill></span>
                                    <span class="btn-text">
                                      <svg class="icon" viewBox="0 0 14 14" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M7 1V13M13 7H1"></path>
                                      </svg>
                                    </span>
                                  </button>
                                  <quick-view id="Quickview-${widgetId}-${product.id}" class="quick-view x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" data-product-url="${url}" role="dialog" aria-label="{{ 'products.product.choose_product_options' | t: product_name: product.title | escape }}" aria-modal="true">
                                    <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="Quickview-${widgetId}-${product.id}" aria-expanded="false"></overlay-element>
                                    <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
                                      <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
                                        <button class="button button--secondary button--close drawer__close z-1 absolute top-0 right-0 flex items-center justify-center opacity-0" type="button" is="hover-button" aria-controls="Quickview-${widgetId}-${product.id}" aria-expanded="false" aria-label="{{ 'general.accessibility.close' | t | escape }}">
                                          <span class="btn-fill" data-fill></span>
                                          <span class="btn-text">
                                            <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                                            </svg>
                                          </span>
                                        </button>
                                      </gesture-element>
                                      <div class="quick-view__content drawer__content opacity-0 invisible flex flex-col h-full grow shrink"></div>
                                    </div>
                                  </quick-view>
                                </div>
                                <div class="flex flex-col">
                                  <a href="${url}" class="rebuy-product-title text-base">${title}</a>
                                  <a href="${url}" class="rebuy-product-price text-base">${price}</a>   
                                  <div type="submit" class="quick-atc" data-variant-id="${product.selected_variant_id}">Add to cart</div>
                                </div>
                              </div>      
                              `;
      }
    });
    if (container.classList.contains('rebuy-widget-products-primary')) {
      container.closest('.rebuy-widget-primary').classList.remove('hidden');
      if (total_products > 4) {
        container.classList.add('rebuy-widget-carousel');
        setTimeout(function() {
          rebuyWidgetCarousel('.rebuy-widget-primary');
        }, 1000);
      } else {
        container.classList.remove('rebuy-widget-carousel');
      }
    }
    if (container.classList.contains('rebuy-widget-products-top')) {
      container.closest('.rebuy-widget-top').classList.remove('hidden');
      setTimeout(function() {
        rebuyWidgetCarousel('.rebuy-widget-top');
      }, 1000);
    }
    if (container.classList.contains('rebuy-widget-products-secondary')) {
      container.closest('.rebuy-widget-secondary').classList.remove('hidden');
    }
  }
}

function rebuyWidgetShuffle() {
  const shuffleButton = document.querySelector(".rebuy-secondary-shuffle");
  const tabs = document.querySelectorAll(".rebuy-secondary-tabs .tab__item");
  const widgetTitle = document.querySelector(".rebuy-secondary-shuffle-title span");

  function updateTitle(tab) {
    if (tab) {
      widgetTitle.textContent = tab.getAttribute("data-title");
    }
  }
  function clickRandomTab() {
    if (tabs.length > 0) {
      const randomTab = tabs[Math.floor(Math.random() * tabs.length)];
      randomTab.click();
    }
  }
  function shuffleClick() {
    let firstEnabledTab = tabs[0];
    let clicked = false;
    for (let i = 0; i < tabs.length; i++) {
      if (tabs[i].hasAttribute("disabled")) {
        if (i === tabs.length - 1) {
          firstEnabledTab.click(); // Loop to first tab if last is disabled
          updateTitle(firstEnabledTab);
        } else {
          tabs[i + 1].click();
          updateTitle(tabs[i + 1]);
        }
        clicked = true;
        break;
      }
    }
    if (!clicked) {
      firstEnabledTab.click(); // Click first tab if no disabled tabs exist
      updateTitle(firstEnabledTab);
    }
  }
  tabs.forEach(tab => {
    tab.addEventListener("click", function () {
      updateTitle(tab);
    });
  });
  shuffleButton.addEventListener("click", shuffleClick);
  clickRandomTab();
}

document.addEventListener('rebuy.init', function(event){
  if(event.detail.widget.id != 7079) {
    buildRebuyWidget(event.detail.widget.id, event.detail.widget.data.products);
    console.log(event.detail.widget);
  }
});

document.addEventListener('cart:updated', function(event){
  window.Rebuy.widgets.forEach(widget => {
    if(widget.data.id != 7079) {
      buildRebuyWidget(widget.data.id, widget.data.products);
    }
  });
  rebuyWidgetShuffle();
  rivoReload();
});

document.addEventListener("DOMContentLoaded", function () {
  rebuyWidgetShuffle();
});


/*document.addEventListener('rebuy.loaded', (event) => {
  console.log('rebuy-loaded');
  const options = {method: 'GET'}; 
  const cache_key = window.Rebuy.shop.cache_key;
  const api_key = encodeURIComponent(window.Rebuy.shop.api_key);
  const country_code = document.querySelector(`#localization-drawer input[name="country_code"]`).value;
  console.log('country code: ' + country_code);
  fetch(`https://rebuyengine.com/api/v1/custom/id/35472?format=pretty&country_code=${country_code}&limit=10&key=${api_key}`, options)
    .then(res => res.json())
    .then(res => buildRebuyWidget(35472, res.data))
    .then(res => console.log(res))
    .catch(err => console.error(err));
});*/

/* Disabled ATC button behavior */
function disabledAtcFeedback() {
  const atcButton = document.querySelector('.product-form .product-form__submit');
  if (atcButton != null) {    
    atcButton.addEventListener("click", function (e) {
      let isValid = true;
      document.querySelectorAll(".product-form__input.variant-input-wrapper").forEach(function (optionGroup) {
        if (!optionGroup.querySelector("input[type='radio']:checked")) {
          optionGroup.classList.add("not-selected");
          isValid = false;
        } else {
          optionGroup.classList.remove("not-selected");
        }
      });
      if (!isValid) {
        e.preventDefault(); // Prevent form submission if any required option is missing
      }
    });
  }
  document.querySelectorAll(".product-form__input input[type='radio']").forEach(function (radio) {
    radio.addEventListener("change", function () {
      this.closest(".product-form__input").classList.remove("not-selected");
    });
  });
 
}

document.addEventListener("DOMContentLoaded", function () {
  disabledAtcFeedback();
});

/* Review stars - remove the word 'reviews' + load reviews in a description block */
function updateReviews() {
  const targetNode = document.querySelector('.ruk_rating_snippet');
  const descriptionReviewsBlock = document.querySelector('.description-block-reviews-text');
  const descriptionReviewsBlockScore = document.querySelector('.description-block-reviews-score');

  if (!targetNode || !descriptionReviewsBlock || !descriptionReviewsBlockScore) return;

  const applyUpdates = () => {
    const countEl = targetNode.querySelector('.ruk-rating-snippet-count');
    const reviewsDrawer = document.querySelector('#ProductReviewsDrawer');

    if (countEl && reviewsDrawer) {
      countEl.innerHTML = countEl.innerHTML.replace(/\sReviews?/, '');
      const leftGroup = reviewsDrawer.querySelector('.header__group.header__group--left');
      const centerGroup = reviewsDrawer.querySelector('.header__group.header__group--center .R-SliderIndicator-group');

      if (leftGroup) descriptionReviewsBlock.innerHTML = leftGroup.innerHTML;
      if (centerGroup) {
        descriptionReviewsBlockScore.innerHTML = centerGroup.innerHTML;
        descriptionReviewsBlockScore.nextElementSibling.innerHTML = 'View All Reviews';
      }
    }
  };

  // If content is already loaded, apply immediately
  if (targetNode.querySelector('.ruk-rating-snippet-count')) {
    applyUpdates();
    return;
  }

  // Otherwise, observe for changes
  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        applyUpdates();
        observer.disconnect();
        break;
      }
    }
  });

  observer.observe(targetNode, { childList: true, subtree: true });
}

document.addEventListener("DOMContentLoaded", updateReviews);

/* Review stars - remove the word 'reviews' + load reviews in a description block */
function homepageReviewsBlock() {
  const targetNode = document.querySelector('#ReviewsWidgetHomepage');
  const descriptionReviewsBlock = document.querySelector('.description-block-reviews-text');
  const descriptionReviewsBlockScore = document.querySelector('.description-block-reviews-score');

  if (!targetNode) return;

  const applyUpdates = () => {
    const reviewsContent = targetNode.querySelector('.R-ContentList');

    if (reviewsContent) {
      reviewsContent.insertAdjacentHTML('beforeend', '<div class="R-ContentList__item u-textLeft--all reviews-link-block"><a class="button button--primary" href="/pages/testimonials-1">View All Reviews</div>');
      
    }
  };

  // If content is already loaded, apply immediately
  if (targetNode.querySelector('.R-ContentList')) {
    applyUpdates();
    return;
  }

  // Otherwise, observe for changes
  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        applyUpdates();
        observer.disconnect();
        break;
      }
    }
  });

  observer.observe(targetNode, { childList: true, subtree: true });
}

document.addEventListener("DOMContentLoaded", homepageReviewsBlock);

/* Color swatch - section renderring API [TEST VERSION, REQUIRES TESTING AND ADJUSTMENT - I'LL FINISH IT LATER, PROBABLY IN PHASE 2] */
/*function attachSwatchListeners() {
  document.querySelectorAll('.color-swatch-custom').forEach(swatch => {
    swatch.addEventListener('click', function (e) {
      e.preventDefault();

      const productHandle = this.dataset.handle;
      const sectionId = this.dataset.sectionId;

      // Build the URL for the Section Rendering API
      const url = `/products/${productHandle}?section_id=${sectionId}`;

      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch section content');
          }
          return response.text();
        })
        .then(html => {
          // Parse and update the section
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(html, 'text/html');
          const newSection = newDoc.querySelector(`#shopify-section-${sectionId}`);
          const currentSection = document.querySelector(`#shopify-section-${sectionId}`);

          if (newSection && currentSection) {
            currentSection.innerHTML = newSection.innerHTML;

            // Reattach swatch event listeners
            attachSwatchListeners();
          }

          // Reinitialize any third-party scripts
        })
        .catch(error => {
          console.error('Error updating product section:', error);
        });
    });
  });
}*/

/* SHOQ back in stock function */
function stoqBiS() {
  let notifyMeBtn = document.getElementById('notify-me-btn');
  let atcButtonContainer = document.querySelector('.product-form__atc-wishlist');
  if (atcButtonContainer) {
    let productData = atcButtonContainer.dataset.product;
    let parsedProduct = JSON.parse(productData);  
    var variantSelector = document.querySelector('.product-form [name="id"]');
    if (notifyMeBtn) {
      notifyMeBtn.addEventListener('click', function() {
        const variantId = parseInt(variantSelector.value);
        console.log('variant ID: ' + variantId);
        window._RestockRocket.openModal(parsedProduct, variantId);
      });
    }
  }
}
document.addEventListener("DOMContentLoaded", stoqBiS);

function attachSwatchListeners() {
  const container = document.querySelector('.product-page-color-swatches');
  if (!container) return;
  container.removeEventListener('click', swatchClickHandler);
  container.addEventListener('click', swatchClickHandler);
}

function swatchClickHandler(e) {
  const swatch = e.target.closest('.color-swatch-custom');
  if (!swatch) return;

  e.preventDefault();

  const productHandle = swatch.dataset.handle;
  const sectionId = swatch.dataset.sectionId;

  const newUrl = `/products/${productHandle}`;
  window.history.replaceState({ path: newUrl }, '', newUrl);

  const url = `/products/${productHandle}?section_id=${sectionId}`;

  fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch section content');
      }
      return response.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');

      // Elements to update
      const elementsToUpdate = [
        '.product-form-wrapper',
        '.product__gallery',
        '.product__price',
        '.description-container',
        '.product__badge-message',
        '.swatches-group-limited',
        '.swatches-group-core',
        '.variant-picker',
        'noscript',
        'style[data-product-style]',
        '.product-content-description',
        'sticky-buy-button',
        '#product-styled-with',
        'script[type="application/ld+json"]',
        'script[data-boldmetrics-script]'
      ];

      elementsToUpdate.forEach(selector => {
        const newElement = newDoc.querySelector(selector);
        const currentElement = document.querySelector(selector);
        if (newElement && currentElement) {
          currentElement.replaceWith(newElement);
        }
      });

      // Update swatch active state
      swatch.closest('.product-page-color-swatches').querySelectorAll('.color-swatch-custom').forEach(el => {
        el.classList.remove('active-swatch');
      })
      swatch.classList.add('active-swatch');

      // Reinitialize  third-party scripts below (TBD)
      
      // Variant buttons
      document.querySelectorAll('variant-radios').forEach(el => {
        new VariantRadios(el);
      });
      // Product description trigger and dropdowns
      productDescription();
      // Boldmetrics
      boldmetricsReload();
      // Disabled ATC feedback/alert
      disabledAtcFeedback();
      // Custom Zoom
      customZoom();
      // rivoReload
      rivoReload();
      // updateReviews
      updateReviews();
      // Sticky ATC anchor link
      stickyAtcAnchor();
      /* STOQ Back in stock */
      stoqBiS();
      
    })
    .catch(error => {
      console.error('Error updating product section:', error);
    });
}
attachSwatchListeners();

/* Revo and Stoq reload in quick view */
document.addEventListener('quickview:loaded', function() {
  rivoReload();
  disabledAtcFeedback();
  stoqBiS();
})

/* Product key callouts */
function keyCallouts() {
  let callouts = document.querySelectorAll('.product-key-callouts button');
  if (callouts != null) {
    callouts.forEach(callout => {
      callout.addEventListener('click', function() {
        let tabTarget = callout.dataset.tab;
        let tabTargetEl = document.querySelector(`#ProductCalloutsDrawer .tab__item[data-tab="${ tabTarget}"]`);
        if(tabTargetEl != null) {
          tabTargetEl.click();
        }
      });
    });
  }
}
keyCallouts();

/* Anchor link scroll */
function anchorLinkScroll(selector = 'a[href^="#"]') {
  document.addEventListener('click', function (e) {
    const link = e.target.closest(selector);
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href.length <= 1 || !href.startsWith('#')) return;

    const targetId = href.slice(1);
    const targetEl = document.getElementById(targetId);
    if (targetEl) {
      e.preventDefault();
      targetEl.scrollIntoView({ behavior: 'smooth' });
    }
  });
}
anchorLinkScroll();

/* About us - gender toggle */
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.sticky-nav-gender-toggle');
  const sections = document.querySelectorAll('#MainContent .shopify-section');

  if(container != null) {
    container.addEventListener('click', (e) => {
      const button = e.target.closest('.gender-toggle-button');
      if (!button) return;
  
      const gender = button.getAttribute('data-gender');
      const otherGender = gender === 'men' ? 'women' : 'men';
  
      container.setAttribute('data-active-gender', gender);
  
      const url = new URL(window.location);
      url.searchParams.set('gender', gender);
      history.pushState({}, '', url);
  
      const showSections = button.getAttribute('data-sections').split(',').map(i => parseInt(i.trim(), 10) - 1);
      const otherButton = container.querySelector(`.gender-toggle-button[data-gender="${otherGender}"]`);
      const hideSections = otherButton.getAttribute('data-sections').split(',').map(i => parseInt(i.trim(), 10) - 1);
  
      showSections.forEach(i => {
        if (sections[i]) {
          sections[i].classList.remove('hide-section');
          sections[i].classList.add('show-section', 'section--padding');
        }
      });
  
      hideSections.forEach(i => {
        if (sections[i]) {
          sections[i].classList.add('hide-section');
          sections[i].classList.remove('show-section', 'section--padding');
        }
      });
    });
  }
});

/* PLP product gallery switch */
/*document.addEventListener("DOMContentLoaded", () => {
  if (window.innerWidth > 767) return;

  const carouselEls = document.querySelectorAll('.card-grid .product-card:first-child .product-card__carousel');
  if (!carouselEls.length) return;

  function triggerCarouselAnimation(el) {
    const nextArrow = el.querySelector('.flickity-prev-next-button.next');
    const prevArrow = el.querySelector('.flickity-prev-next-button.previous');

    if (nextArrow && prevArrow) {
      nextArrow.click();
      setTimeout(() => {
        prevArrow.click();
      }, 1000);
    }
  }

  function waitForFlickityEnabled(el) {
    if (el.classList.contains('flickity-enabled')) {
      triggerCarouselAnimation(el);
      return;
    }

    const observer = new MutationObserver((mutations, obs) => {
      mutations.forEach(m => {
        if (m.attributeName === 'class' && el.classList.contains('flickity-enabled')) {
          triggerCarouselAnimation(el);
          obs.disconnect();
        }
      });
    });
    observer.observe(el, { attributes: true });
  }

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          waitForFlickityEnabled(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -50% 0px',
      threshold: 0
    });

    carouselEls.forEach(el => io.observe(el));
  } else {
    carouselEls.forEach(el => waitForFlickityEnabled(el));
  }
});*/
document.addEventListener("DOMContentLoaded", () => {
  if (window.innerWidth > 767) return;

  const carouselEls = document.querySelectorAll('.card-grid .product-card:first-child .product-card__carousel');
  if (!carouselEls.length) return;

  function triggerScrollAnimation(el) {
    const scrollDistance = el.scrollWidth * 0.2;
    el.scrollTo({ left: 0, behavior: 'auto' }); 
    setTimeout(() => {
      el.scrollTo({ left: scrollDistance, behavior: 'smooth' });
      setTimeout(() => {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      }, 1000);
    }, 300);
  }

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          triggerScrollAnimation(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -50% 0px',
      threshold: 0
    });

    carouselEls.forEach(el => io.observe(el));
  } else {
    carouselEls.forEach(el => triggerScrollAnimation(el));
  }
});

/* About us - image with mask test */
function pathToDots(path, dotSpacing = 10, dotRadius = 1) {
  const totalLength = path.getTotalLength();
  const svg = path.ownerSVGElement;
  const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  group.classList.add('dot-group');

  for (let dist = 0; dist <= totalLength; dist += dotSpacing) {
    const point = path.getPointAtLength(dist);
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', point.x);
    circle.setAttribute('cy', point.y);
    circle.setAttribute('r', dotRadius);
    circle.setAttribute('fill', path.getAttribute('stroke') || '#000');
    circle.setAttribute('opacity', '0');  // initially invisible
    group.appendChild(circle);
  }

  svg.appendChild(group);
  path.remove(); // remove original path
}

function animateDots(group) {
  const dots = group.querySelectorAll('circle');
  dots.forEach((dot, i) => {
    setTimeout(() => {
      dot.style.transition = 'opacity 0.3s';
      dot.style.opacity = 1;
    }, i * 30);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const maskSvg = document.querySelector('#image-with-mask svg');
    if (maskSvg) {
      const paths = maskSvg.querySelectorAll('path');
      paths.forEach(path => pathToDots(path, 10, 1));
    
  
    // Unified IntersectionObserver
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
  
          if (el.matches('g.dot-group')) {
            animateDots(el);
          } else if (el.matches('.image-with-mask-desktop-hotspot, .image-with-mask-mobile-hotspot')) {
            el.classList.add('visible');
          }
  
          observer.unobserve(el);
        }
      });
    }, {
      root: null,
      threshold: 0,
      rootMargin: '-30% 0px -30% 0px'
    });
  
    // Observe all targets
    document.querySelectorAll('g.dot-group, .image-with-mask-desktop-hotspot, .image-with-mask-mobile-hotspot')
      .forEach(el => observer.observe(el));
  
    let mobileHotspotTrigger = document.querySelectorAll('.image-with-mask-mobile-hotspot');
    if (mobileHotspotTrigger.length > 0) {
      mobileHotspotTrigger.forEach(trigger => {
        trigger.addEventListener('click', () => {
          let index = trigger.dataset.hotspot;
          console.log('test: ' + index);
          let targetEl = document.querySelector(`.image-with-mask-mobile-drawer-element[data-hotspot="${index}"]`);
          document.querySelectorAll('.image-with-mask-mobile-hotspot').forEach(el => {
            el.classList.remove('active');
          });
          document.querySelectorAll('.image-with-mask-mobile-drawer-element').forEach(el => {
            el.classList.remove('active');
          });
          trigger.classList.add('active');
          targetEl.classList.add('active');
             
          setTimeout(function() {
            let offset = targetEl.offsetTop - 80;
            console.log(offset);
            document.querySelector('#image-with-mask-drawer .drawer__scrollable').scrollTo({
                top: offset,
                behavior: "instant"
              });
          }, 400);
        });
      });
    }
  }
  
});

/* Rivo ATC fix */
document.body.addEventListener('click', function (e) {
  const atcBtn = e.target.closest('[rivo-ax-on\\:click^="addToCartFromButton"]');
  if (!atcBtn) return;
  setTimeout(async () => {
    const cartDrawer = document.querySelector('#CartDrawer');
    if (!cartDrawer) return;

    const sectionID = cartDrawer.getAttribute('data-section-id');

    const parsedState = await fetch(`/?sections[]=${encodeURIComponent(sectionID)}`)
      .then(res => res.json());

    const cartJson = await fetch(`${theme.routes.cart_url}`, {
      ...theme.utils.fetchConfig()
    }).then(res => res.json());

    cartJson.sections = parsedState;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartJson
    });

    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: {
        product: parsedState
      }
    }));
  }, 2000);
});

/* About - Our fit - height toggle section */
function aboutHeightToggle() {
  let aboutHeightBtn = document.querySelectorAll('.about-heights-nav-button');
  if(aboutHeightBtn) {
    aboutHeightBtn.forEach(button => {
      let aboutHeightContainer = button.closest('.about-height-container');
      button.addEventListener('click', () => {
        if (button.classList.contains('active')) {
          button.classList.remove('active');
          aboutHeightContainer.dataset.size = '';
        } else {
          aboutHeightContainer.querySelectorAll('.about-heights-nav-button').forEach(btn => {
            btn.classList.remove('active');
          });
          aboutHeightContainer.dataset.size = button.dataset.size;
          button.classList.add('active');
        }
      });
    });
  }
}
document.addEventListener('DOMContentLoaded', () => {
  aboutHeightToggle();
});

/* Quick view swatches */
document.addEventListener('click', function (e) {
  const swatch = e.target.closest('quick-view[open] .color-swatch-custom');
  if (swatch) {   
    e.preventDefault();
    const quickViewModal = swatch.closest('quick-view');
    const productUrl = swatch.getAttribute('href');

    // Set new URL for the modal
    quickViewModal.dataset.productUrl = productUrl;

    // Show loading state if needed
    const drawerContent = quickViewModal.querySelector(quickViewModal.selector);
    drawerContent.classList.add('loading');

    // Reload quickview content
    quickViewModal.quickview();
  }
});

/* Lookbook hotspots - old theme import fix  */
document.addEventListener('DOMContentLoaded', () => {
  let hotspotElements = document.querySelectorAll('.product-trigger');
  hotspotElements.forEach(element => {
    element.addEventListener('click', () => { 
      let drawerTrigger = document.querySelector('.lookbook-drawer-button');
      let outputContainer = document.querySelector('#slideshow-products-lookbook .slideshow-drawer-content');
      let productContainer = element.closest('.product-drawer-container');
      let productIdsData = productContainer.dataset.productIds;
      let productIds = productIdsData ? String(productIdsData).split(",") : [];
          
      outputContainer.innerHTML = '';

      async function getProductsByIds(ids) {
        const search = encodeURI(ids.map(id => {
            return `id:${id}`;
        }).join(" OR "));
        await fetch(`/search?q=${search}&type=product&view=grid-ajax-lookbook`)
        .then((response) => {
          return response.text();
        })
        .then((html) => {
          console.log(html);
          outputContainer.innerHTML = html;
        });
      }
    
      getProductsByIds(productIds);

      drawerTrigger.click();
    });
  });
});

/* Multicolumn slider */
let multicolumnSliderEl = document.querySelectorAll('.multicolumn-slider');
if (multicolumnSliderEl) {
  multicolumnSliderEl.forEach(slider => {
    multicolumnSlider = new Flickity(slider, {
      pageDots: false,
      prevNextButtons: true,
      freeScroll: true,
      wrapAround: false,
      cellAlign: 'center',
      contain: true,
      arrowShape: { 
        x0: 15,
        x1: 60, y1: 40,
        x2: 65, y2: 35,
        x3: 25
      },
      on: {
        ready: function() {
          setTimeout(() => this.element.setAttribute('loaded', ''));
        }
      }
    });

    function toggleArrows() {
      var carouselWidth = multicolumnSlider.viewport.offsetWidth;
      var cellsWidth = 0;
      multicolumnSlider.cells.forEach(function(cell) {
        cellsWidth += cell.size.width;
      });

      if (cellsWidth <= carouselWidth) {
        slider.classList.add('hide-arrows');
      } else {    
        slider.classList.remove('hide-arrows');
      }
    }
    // Initial check
    toggleArrows();
    window.addEventListener('resize', toggleArrows);
  });
}

/* Rebuy attribution */
function addAttributionToForm(form, searchParams) {
  if (!form || !searchParams) return;

  const attribute_keys = ["_source", "_widget_id", "_attribution"];
  attribute_keys.forEach(key => {
    const val = searchParams.get(key);
    if (!val) return;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `properties[${key}]`;
    input.value = val;
    form.appendChild(input);
  });
}

/* Main product form */
document.addEventListener("DOMContentLoaded", () => {
  const form = document.querySelector(".product-form-wrapper .product-form");
  if (!form) return;

  const params = new URLSearchParams(window.location.search);
  addAttributionToForm(form, params);
});

/* Quick view */
document.addEventListener("quickview:loaded", (event) => {
  const modal = document.querySelector("quick-view[open]");
  if (!modal) return;

  const form = modal.querySelector(".product-form-wrapper .product-form");
  const productUrl = event.detail?.productUrl || "";
  const query = productUrl.includes("?") ? productUrl.split("?")[1] : "";
  const params = new URLSearchParams(query);
  addAttributionToForm(form, params);
});